/* LAV TDMS — просмотр документа и разметка «красным карандашом». Три варианта
   раскладки: «Классический» (правая полка замечаний) · «Фокус» (плавающие
   инструменты) · «Сравнение» (версия против версии).

   Всё, что здесь показано, приходит с сервера. Раньше лист рисовался
   заглушкой-чертежом, файл брался из блоба текущей вкладки, а булавки
   существующих замечаний расставлялись по формуле от порядкового номера —
   к самому чертежу они отношения не имели. */
import { Fragment, useCallback, useEffect, useLayoutEffect, useState, useRef, useMemo, type CSSProperties, type ReactNode } from 'react'
import { Avatar, Badge, Button, Input, Select, Segmented, EmptyState, Spinner, Tooltip, clickable, toast, ResizeHandles, useResizable } from '@/ui'
import type { Resizable } from '@/ui'
import { Icon } from '@/ds/icons'
import { CritBadge } from '@/screens/pt/CrsSheet'
import { request, requestFile, saveFile } from '@/data/api'
import { useMe } from '@/data/meta'
import { qk, useItem, useList, useSub, inboxKeys } from '@/data/queries'
import { useCommand } from '@/data/mutations'
import type { Crs, Doc, DocSubmission, DocVersion, Revision, Vdoc } from '@/data/types'
/* Перевод «версия файла → подача» и подпись набора — те же, что в карточке
   документа: две витрины одного и того же не должны считать по-разному. */
import { submissionOf, versionLabel } from '@/screens/pt/DocumentCard'
import { t, type Lang } from '@/i18n/lang'
import { revVer } from '@/lib/format'
import { previewKind, sanitizeHtml } from '@/lib/preview'


/* pdf.js подключается динамическим импортом, поэтому его типы недоступны
   статически. Описываем ровно то, чем пользуемся. */
interface PdfViewport { width: number; height: number }
/** Отрисовка листа: её обязательно отменять — сама она не прекращается. */
interface PdfRenderTask { promise: Promise<void>; cancel: () => void }
interface PdfPage {
  getViewport: (o: { scale: number }) => PdfViewport
  render: (o: { canvas: HTMLCanvasElement; viewport: PdfViewport }) => PdfRenderTask
  /** Освобождает разобранное содержимое листа — иначе оно висит до закрытия файла. */
  cleanup: () => void
}
interface PdfDocument {
  numPages: number
  getPage: (n: number) => Promise<PdfPage>
}
/**
 * Задача открытия файла. Закрывать нужно ЕЁ, а не документ: в pdfjs-dist 6
 * `destroy()` есть только у задачи, а у самого документа его нет. Код звал его
 * у документа — очистка падала `destroy is not a function`, а вместе с ней
 * рушилось всё поддерево: возврат из просмотрщика в реестр давал белый экран.
 */
interface PdfLoadingTask {
  promise: Promise<PdfDocument>
  /** Закрывает файл и его воркер. Без этого вкладка держит все открытые ревизии. */
  destroy: () => Promise<void>
}
interface PdfJs {
  GlobalWorkerOptions: { workerSrc: string; workerPort: Worker | null }
  getDocument: (o: { data: ArrayBuffer }) => PdfLoadingTask
}

/* Система координат разметки. В базе — доли листа [0..1]: пиксели зависят от
   зума и DPI, при которых их поставили, и на другом экране означают другое
   место. На экране удобнее целые, поэтому доли разворачиваются в этот viewBox. */
const VB_W = 1000
const VB_H = 707

interface Pt { x: number; y: number }
interface Shape {
  t: string
  pts?: Pt[]
  x0?: number
  y0?: number
  x1?: number
  y1?: number
  x?: number
  y?: number
  label?: string
}
/** Фигура, как её отдаёт сервер. */
/** Фигура листа со всем, что решает её судьбу: чья она и к чему привязана. */
interface Piece {
  id: string
  s: Shape
  crs: string | null
  mine: boolean
  /* Замечание снято утверждающим или закрыто: обводка остаётся историей, но
     действующей её показывать нельзя — следующий рецензент писал по ней то же
     замечание заново. */
  stale?: boolean
}

interface StoredMarkup {
  id: string
  page: number
  kind: string
  geom: Record<string, unknown>
  label: string | null
  crs: string | null
  author: string
  /* internal — карандаш стороны заказчика, shared — видно всем, кто видит
     документ. У разметки с замечанием роли не играет: её открывает выпуск листа. */
  visibility: string
  /* Судьба привязанного замечания — от сервера: клиент не должен решать по
     своей копии, снято оно или закрыто. */
  crsVerdict?: string | null
  crsStatus?: string | null
}
/**
 * Метка на листе — фигура, к которой привязано замечание. Раньше ею могла быть
 * только булавка; облако и стрелку обвести было можно, а связать с замечанием
 * нечем, хотя обводят как раз для этого.
 */
interface Mark {
  id: string
  n: number
  kind: string
  x: number
  y: number
  crs: Crs | null
  code: string | null
}

const TOOLS = [
  { id: 'cursor', icon: 'mouse-pointer-2', ru: 'Курсор' },
  { id: 'pen', icon: 'pencil', ru: 'Карандаш' },
  { id: 'rect', icon: 'square', ru: 'Прямоугольник' },
  { id: 'cloud', icon: 'cloud', ru: 'Облако ревизии' },
  { id: 'arrow', icon: 'move-up-right', ru: 'Стрелка' },
  { id: 'text', icon: 'type', ru: 'Текст' },
  { id: 'pin', icon: 'map-pin', ru: 'Замечание' },
  { id: 'stamp', icon: 'stamp', ru: 'Штамп' },
]
const STAMPS = ['AFD', 'IFC', 'REVISE', 'VOID']

/** Почему нельзя снять фигуру. Гаснущая кнопка без объяснения хуже её отсутствия. */
export function eraseHint(picked: StoredMarkup | null, me: string | undefined, lang: Lang): string {
  if (!picked) return t(lang, 'Выберите фигуру курсором, чтобы снять её', 'Pick a shape with the cursor to remove it')
  if (picked.crs) return t(lang, 'Фигура несёт замечание — снимается вместе с ним', 'The shape carries a comment and goes with it')
  if (picked.author !== me) return t(lang, 'Пометка чужая', 'Someone else drew this')
  return t(lang, 'Снять выбранную фигуру', 'Remove the selected shape')
}

/** Названия фигур для правой полки: там говорится, к чему привязано замечание. */
const SHAPE_NAME: Record<string, { ru: string; en: string }> = {
  pen: { ru: 'линия', en: 'line' },
  rect: { ru: 'прямоугольник', en: 'rectangle' },
  cloud: { ru: 'облако', en: 'cloud' },
  arrow: { ru: 'стрелка', en: 'arrow' },
  text: { ru: 'текст', en: 'text' },
  stamp: { ru: 'штамп', en: 'stamp' },
}

// ---- перевод геометрии между базой и экраном ---------------------------
const num = (v: unknown): number => (typeof v === 'number' ? v : 0)

/** Доли листа → координаты viewBox. */
function toShape(m: StoredMarkup): Shape {
  const g = m.geom ?? {}
  const base: Shape = { t: m.kind, label: m.label ?? undefined }
  if (m.kind === 'pen') {
    const pts = Array.isArray(g.pts) ? (g.pts as Pt[]) : []
    return { ...base, pts: pts.map((p) => ({ x: num(p.x) * VB_W, y: num(p.y) * VB_H })) }
  }
  if (m.kind === 'rect' || m.kind === 'cloud' || m.kind === 'arrow') {
    return { ...base, x0: num(g.x0) * VB_W, y0: num(g.y0) * VB_H, x1: num(g.x1) * VB_W, y1: num(g.y1) * VB_H }
  }
  return { ...base, x: num(g.x) * VB_W, y: num(g.y) * VB_H }
}

/**
 * Точка, у которой рисуется номер метки. Для обводки это её левый верхний угол,
 * для стрелки — остриё (она указывает на место, а не занимает его), для
 * карандаша — начало линии.
 */
function anchorOf(s: Shape): Pt {
  if (s.t === 'pen') return s.pts?.[0] ?? { x: 0, y: 0 }
  if (s.t === 'rect' || s.t === 'cloud') return { x: Math.min(s.x0 ?? 0, s.x1 ?? 0), y: Math.min(s.y0 ?? 0, s.y1 ?? 0) }
  if (s.t === 'arrow') return { x: s.x1 ?? 0, y: s.y1 ?? 0 }
  return { x: s.x ?? 0, y: s.y ?? 0 }
}

/** Координаты viewBox → доли листа. */
function toGeom(s: Shape): Record<string, unknown> {
  if (s.t === 'pen') return { pts: (s.pts ?? []).map((p) => ({ x: p.x / VB_W, y: p.y / VB_H })) }
  if (s.t === 'rect' || s.t === 'cloud' || s.t === 'arrow') {
    return { x0: (s.x0 ?? 0) / VB_W, y0: (s.y0 ?? 0) / VB_H, x1: (s.x1 ?? 0) / VB_W, y1: (s.y1 ?? 0) / VB_H }
  }
  return { x: (s.x ?? 0) / VB_W, y: (s.y ?? 0) / VB_H }
}

// ---- файл версии -------------------------------------------------------
/**
 * Адрес файла версии. Сервер отдаёт подписанную ссылку телом ответа, и уже по
 * ней идёт браузер: переход по адресу API не несёт токена, а подпись хранилища
 * покрывает адрес целиком — любой лишний заголовок её ломает.
 */
async function sheetUrl(path: string): Promise<{ url: string; notice: string | null }> {
  const link = await request<{ url: string | null; notice?: string | null; controlled?: string | null }>('GET', path)
  /* Недействующая ревизия показывается ПОМЕЧЕННОЙ. Просмотрщик читал из ответа
     только «url» и пометку не замечал вовсе: заменённая, аннулированная и
     приостановленная ревизия выглядела на экране действующей. Сырой ссылки на
     такой объект сервер больше и не даёт — PDF приходит собранной копией, и её
     надо забрать с токеном. */
  if (link.controlled) {
    const { blob } = await requestFile(link.controlled.replace(/^\/api/, ''))
    return { url: URL.createObjectURL(blob), notice: link.notice ?? null }
  }
  if (!link.url) throw new Error('сервер не отдал адрес файла')
  return { url: link.url, notice: link.notice ?? null }
}

/**
 * Адрес листа для показа.
 *
 * <p>`loading` существует потому, что «ещё не пришло» и «показывать нечего» —
 * разные вещи, а наружу отдавались одинаково: пустой `url` без ошибки. Экран
 * рисовал на этом «Файл не загружен» и советовал приложить файл, который уже
 * приложен. Заметнее всего на НЕДЕЙСТВУЮЩЕЙ ревизии: её сервер отдаёт не
 * ссылкой на хранилище, а собранной копией со штампом, и её клиент качает
 * целиком — секунды, всё это время экран врал.
 */
function useFileUrl(no: string, rev: string | null, ver: number | null):
    { url: string | null; notice: string | null; error: string | null; loading: boolean } {
  /* Ссылка хранится вместе с версией, для которой выдана, и чужую не отдаёт.
     Сброс состояния внутри эффекта случается уже после отрисовки, и один кадр
     новая версия успевала показать адрес предыдущей — на xlsx это выливалось
     в попытку разобрать его как PDF и ошибку в консоли. */
  const key = `${no}|${rev ?? ''}|${ver ?? ''}`
  const [state, setState] = useState<{ key: string; url: string | null; notice: string | null; error: string | null }>(
    { key, url: null, notice: null, error: null })
  useEffect(() => {
    if (!rev || !ver) return
    let cancelled = false
    /* Собранная копия живёт в памяти вкладки, пока показана: адрес blob надо
       отзывать, иначе просмотр десяти ревизий подряд не отдаёт память до
       перезагрузки. */
    let objectUrl: string | null = null
    const path = `/docs/${encodeURIComponent(no)}/revisions/${encodeURIComponent(rev)}/files/${ver}/content`
    ;(async () => {
      try {
        const sheet = await sheetUrl(path)
        if (sheet.url.startsWith('blob:')) objectUrl = sheet.url
        if (cancelled) return
        setState({ key, url: sheet.url, notice: sheet.notice, error: null })
      } catch (e) {
        if (!cancelled) setState({ key, url: null, notice: null, error: e instanceof Error ? e.message : String(e) })
      }
    })()
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [no, rev, ver, key])
  return sheetState(state, key, rev != null && ver != null)
}

/**
 * Что показывать по состоянию запроса.
 *
 * <p>Отдельно от хука, потому что правило здесь одно и его легко перепутать:
 * «адреса нет» значит «грузится», пока не пришла ошибка, — и только когда
 * грузить нечего вовсе (нет ревизии или версии), это «файла нет».
 *
 * <p>Ключ сравнивается по той же причине, что и раньше: сброс состояния внутри
 * эффекта случается уже после отрисовки, и один кадр показывался бы адрес
 * предыдущей версии.
 */
export function sheetState(
  state: { key: string; url: string | null; notice: string | null; error: string | null },
  key: string,
  requested: boolean,
): { url: string | null; notice: string | null; error: string | null; loading: boolean } {
  if (state.key !== key) return { url: null, notice: null, error: null, loading: requested }
  return {
    url: state.url,
    notice: state.notice,
    error: state.error,
    loading: requested && state.url == null && state.error == null,
  }
}

// ---- лист PDF ----------------------------------------------------------
/** Ширина листа при стократном зуме. Высота считается из пропорций страницы. */
const SHEET_W = 880

/**
 * Предел растра по ширине. За ним холст перестаёт помогать и начинает мешать:
 * лист A1 в 3200 px — это уже около тридцати мегабайт памяти, а вдвое больший
 * растр вкладка не переживёт на десятке видимых страниц.
 */
const RASTER_MAX = 3200

/**
 * Одна страница. Холст рисуется под ТЕКУЩИЙ зум, а не в постоянном
 * разрешении: постоянные 1408 px по ширине давали на листе A1 около 42 dpi,
 * и ни выноску, ни размер на нём прочитать было нельзя — увеличение
 * растягивало ту же кашу. Шаг зума огрублён (см. `raster`), поэтому колесо не
 * заставляет перерисовывать лист на каждый щелчок.
 */
function PdfPageCanvas({ doc, n, raster, onAspect }: {
  doc: PdfDocument
  n: number
  /** Во сколько раз растр шире базовой ширины листа. */
  raster: number
  /** Пропорции ЭТОЙ страницы: в альбоме титул A4 и листы A1 лежат вместе. */
  onAspect: (n: number, aspect: number) => void
}) {
  const ref = useRef<HTMLCanvasElement>(null)
  const [ready, setReady] = useState(false)
  useEffect(() => {
    let cancelled = false
    let task: PdfRenderTask | null = null
    let sheet: PdfPage | null = null
    ;(async () => {
      try {
        sheet = await doc.getPage(n)
        if (cancelled) { sheet.cleanup(); return }
        const base = sheet.getViewport({ scale: 1 })
        onAspect(n, base.width / base.height)
        const width = Math.min(RASTER_MAX, SHEET_W * raster)
        const viewport = sheet.getViewport({ scale: width / base.width })
        const canvas = ref.current
        if (!canvas || cancelled) return
        canvas.width = viewport.width
        canvas.height = viewport.height
        task = sheet.render({ canvas, viewport })
        await task.promise
        if (!cancelled) setReady(true)
      } catch (e) {
        /* Отменённая отрисовка — не сбой: лист ушёл с экрана или сменился зум. */
        if (!cancelled) console.error('PdfPageCanvas failed on page', n, e)
      }
    })()
    /* Отрисовка отменяется, а разобранный лист освобождается: без этого
       уехавшая страница дорисовывается в никуда и остаётся в памяти вместе с
       содержимым — пять просмотренных ревизий вкладка держала и не отдавала. */
    return () => {
      cancelled = true
      task?.cancel()
      sheet?.cleanup()
    }
  }, [doc, n, raster, onAspect])
  /* Прозрачный холст до готовности и плавный вход — индикация загрузки, а не
     декоративное появление: при листании, в том числе клавиатурном, мигание
     осознанное — без него не отличить «лист ещё считается» от «лист пустой». */
  return <canvas ref={ref} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', opacity: ready ? 1 : 0, transition: 'opacity var(--duration-slow) var(--ease-out)' }} />
}

/**
 * Документ сплошной лентой. Страницы идут подряд и прокручиваются, но холст
 * заводится только у тех, что близко к экрану: у файла в сотню страниц сотня
 * холстов размером с лист съела бы больше гигабайта. Место занимает пустая
 * рамка правильных пропорций, поэтому полоса прокрутки не прыгает.
 */
/**
 * Что известно про загрузку файла. `percent = null` — размер не назван
 * сервером либо идёт разбор; `total = null` — размер неизвестен, но
 * скачанные байты всё равно считаются и показываются.
 */
interface PdfLoadState {
  done: boolean
  percent: number | null
  read: number
  total: number | null
  failed: boolean
}

function PdfPages({ url, scale, onScale, onPages, onCurrent, onLoading, goto, overlay, lang }: {
  url: string
  scale: number
  onScale: (next: number) => void
  onPages: (n: number) => void
  onCurrent: (n: number) => void
  /** Состояние загрузки уходит наверх: тулбар обязан говорить то же, что лист. */
  onLoading: (state: PdfLoadState) => void
  /** `y` — доля высоты листа: по ней лист встаёт так, чтобы точка оказалась
      в середине окна. Без неё переход попадает только на начало листа. */
  goto: { page: number; nonce: number; y?: number | null }
  overlay: (page: number) => ReactNode
  lang: Lang
}) {
  const [doc, setDoc] = useState<PdfDocument | null>(null)
  const [count, setCount] = useState(0)
  const [aspect, setAspect] = useState(SHEET_W / 622)
  /* Пропорции КАЖДОГО листа. Прежде брались у первой страницы и применялись ко
     всем: в альбоме «титул A4 + триста листов A1» рамка листа врала о его
     форме, и слой разметки ложился на неё, а не на чертёж. */
  const [aspects, setAspects] = useState<Record<number, number>>({})
  const [err, setErr] = useState(false)
  /* Доля скачанного и байты. percent = null — сервер не назвал размер (нет
     Content-Length): тогда честнее показать «идёт загрузка» и число скачанных
     байт, чем выдуманный процент. */
  const [progress, setProgress] = useState<{ percent: number | null; read: number; total: number | null }>(
    { percent: 0, read: 0, total: null })
  const [near, setNear] = useState<number[]>([1])
  const boxRef = useRef<HTMLDivElement>(null)
  const slots = useRef(new Map<number, HTMLDivElement>())
  const io = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    let cancelled = false
    let opened: PdfLoadingTask | null = null
    setDoc(null); setCount(0); setErr(false); setNear([1]); setAspects({})
    setProgress({ percent: 0, read: 0, total: null })
    onLoading({ done: false, percent: 0, read: 0, total: null, failed: false })
    ;(async () => {
      try {
        const pdfjs = await getPdfjs()
        /* Файл читается потоком, а не одним arrayBuffer(): чертёж на два
           десятка мегабайт с площадки грузится секундами, и всё это время
           экран показывал пустой белый лист — неотличимо от «файла нет». */
        const data = await download(url, (p) => {
          if (cancelled) return
          setProgress(p)
          onLoading({ done: false, ...p, failed: false })
        })
        if (cancelled) return
        /* Разбор — своя фаза, доли в ней нет: показывается как «открываем».
           Размер остаётся на экране: он уже известен и продолжает объяснять,
           почему открытие занимает время. */
        setProgress((p) => ({ ...p, percent: null, read: data.byteLength }))
        onLoading({ done: false, percent: null, read: data.byteLength, total: data.byteLength, failed: false })
        const task = pdfjs.getDocument({ data })
        opened = task
        const pdf = await task.promise
        if (cancelled) { void task.destroy(); return }
        /* Пропорции первой страницы — только запасные, пока лист не измерен
           сам: рамка нужна ещё до того, как до него дошла отрисовка. */
        const first = await pdf.getPage(1)
        const v = first.getViewport({ scale: 1 })
        first.cleanup()
        if (cancelled) return
        setAspect(v.width / v.height)
        setDoc(pdf)
        setCount(pdf.numPages)
        onPages(pdf.numPages)
        onLoading({ done: true, percent: 100, read: data.byteLength, total: data.byteLength, failed: false })
      } catch (e) {
        console.error('PdfPages failed:', e)
        if (!cancelled) {
          setErr(true)
          onLoading({ done: true, percent: null, read: 0, total: null, failed: true })
        }
      }
    })()
    /* Открытый файл закрывается. Без этого вкладка держала все просмотренные
       ревизии сразу: пять чертежей по полтораста мегабайт — и она не отдаёт
       память до перезагрузки. */
    return () => {
      cancelled = true
      void opened?.destroy()
    }
  }, [url, onPages, onLoading])

  /* Пропорции листа приходят от его же отрисовки — считать их заранее для
     трёхсот страниц значит разобрать весь альбом ради рамок. */
  const onAspect = useCallback((n: number, value: number) => {
    setAspects((prev) => (prev[n] === value ? prev : { ...prev, [n]: value }))
  }, [])

  /* Окно отрисовки — экран вверх и вниз от видимого. Ушедшие далеко страницы
     теряют холст, и память не растёт с длиной документа. */
  useEffect(() => {
    const root = boxRef.current
    if (!root || !count) return
    const obs = new IntersectionObserver((entries) => {
      setNear((prev) => {
        const s = new Set(prev)
        for (const e of entries) {
          const n = Number((e.target as HTMLElement).dataset.page)
          if (e.isIntersecting) s.add(n)
          else s.delete(n)
        }
        return Array.from(s)
      })
    }, { root, rootMargin: '100% 0px' })
    io.current = obs
    for (const el of slots.current.values()) obs.observe(el)
    return () => { obs.disconnect(); io.current = null }
  }, [count])

  // Текущая — та страница, что пересекает середину области просмотра.
  const ticking = useRef(false)
  const onScroll = () => {
    if (ticking.current) return
    ticking.current = true
    requestAnimationFrame(() => {
      ticking.current = false
      const root = boxRef.current
      if (!root) return
      const mid = root.scrollTop + root.clientHeight / 2
      for (const [n, el] of slots.current) {
        if (el.offsetTop <= mid && el.offsetTop + el.offsetHeight >= mid) { onCurrent(n); return }
      }
    })
  }

  useEffect(() => {
    if (!goto.nonce) return
    const el = slots.current.get(goto.page)
    /* Переход мгновенный, а не плавный. Дело не только в правиле «действие с
       клавиатуры не анимируется» (сюда приходят Enter в поле номера листа и
       кнопки «предыдущий/следующий»): все листы смонтированы в полную высоту, и
       наблюдатель видимости с запасом в экран срабатывает на КАЖДОМ кадре
       прокрутки. Плавный проход по набору в шестьдесят листов монтирует и
       начинает отрисовывать почти каждый промежуточный лист — а отрисовка не
       отменяется. Мгновенный переход даёт один пересчёт видимости в точке
       назначения. */
    const root = boxRef.current
    if (!el || !root) return
    const top = goto.y != null
      ? el.offsetTop + goto.y * el.offsetHeight - root.clientHeight / 2
      : el.offsetTop - 14
    root.scrollTo({ top: Math.max(0, top) })
  }, [goto])

  /* Колесо листает, Ctrl+колесо масштабирует — как в любом просмотрщике.
     Раньше событие гасилось безусловно, и колесо всегда меняло масштаб: пролистать
     документ им было нельзя вовсе.

     Гасится теперь только случай с Ctrl, и гасить его обязательно — иначе браузер
     заберёт жест себе и увеличит всю страницу вместе с интерфейсом. Слушатель
     поэтому неленивый: у ленивого preventDefault не действует. Простое колесо
     не трогаем совсем — прокрутку контейнер делает сам, быстрее и с инерцией.

     Точка под курсором запоминается, чтобы после смены масштаба вернуть её на
     место: без этого лист уезжает и его приходится ловить. */
  const anchor = useRef<{ x: number; y: number; cx: number; cy: number; from: number } | null>(null)
  useEffect(() => {
    const root = boxRef.current
    if (!root) return
    const onWheel = (e: WheelEvent) => {
      // metaKey — тот же жест на macOS.
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      const rect = root.getBoundingClientRect()
      const cx = e.clientX - rect.left
      const cy = e.clientY - rect.top
      anchor.current = { x: root.scrollLeft + cx, y: root.scrollTop + cy, cx, cy, from: scale }
      const next = Math.min(2.5, Math.max(0.5, scale * (e.deltaY < 0 ? 1.12 : 1 / 1.12)))
      if (next === scale) anchor.current = null
      else onScale(next)
    }
    root.addEventListener('wheel', onWheel, { passive: false })
    return () => root.removeEventListener('wheel', onWheel)
  }, [scale, onScale])

  // Правка прокрутки идёт до отрисовки кадра, иначе виден скачок.
  useLayoutEffect(() => {
    const root = boxRef.current
    const a = anchor.current
    if (!root || !a) return
    anchor.current = null
    const k = scale / a.from
    root.scrollTop = a.y * k - a.cy
    root.scrollLeft = a.x * k - a.cx
  }, [scale])

  const w = SHEET_W * scale
  /* Разрешение растра идёт за зумом ступенями по половине: перерисовывать
     видимые листы на каждый щелчок колеса дороже, чем показать их чуть
     крупнее нужного. Полуторный запас к экранному размеру тот же, что был, —
     без него тонкая линия чертежа рябит. */
  const raster = Math.ceil(scale * 1.6 * 2) / 2
  return (
    <div ref={boxRef} onScroll={onScroll}
         style={{ position: 'relative', flex: 1, minWidth: 0, overflow: 'auto', background: 'var(--slate-800)', padding: 28 }}>
      {err ? (
        <div style={{ display: 'grid', placeItems: 'center', height: '100%', color: 'var(--slate-300)', fontFamily: 'monospace', fontSize: 13 }}>
          {t(lang, 'Не удалось отобразить PDF', 'Failed to display the PDF')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
          {/* Пока файл идёт, лист пуст и неотличим от «файла нет». Плашка
              говорит, что происходит, и сколько осталось, если размер известен. */}
          {!doc ? (
            <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none' }}>
              <div style={{ display: 'grid', gap: 10, justifyItems: 'center', padding: '18px 22px', borderRadius: 'var(--radius-md)', background: 'rgba(15,23,42,.82)', boxShadow: '0 10px 40px rgba(0,0,0,.45)' }}>
                <SpinIcon size={22} style={{ color: 'var(--amber-300)' }} />
                <span style={{ font: 'var(--font-ui)', fontSize: 13, color: 'var(--slate-100)' }}>
                  {progress.percent == null
                    ? t(lang, 'Открываем документ…', 'Opening the document…')
                    : t(lang, 'Загрузка файла…', 'Downloading the file…')}
                </span>
                {progress.percent != null ? (
                  <span style={{ width: 180, height: 4, borderRadius: 2, background: 'rgba(255,255,255,.18)', overflow: 'hidden' }}>
                    <span style={{ display: 'block', width: progress.percent + '%', height: '100%', background: 'var(--amber-400)', transition: 'width var(--duration-fast) var(--ease-standard)' }} />
                  </span>
                ) : null}
                {/* Размер отвечает на вопрос, который процент оставляет открытым:
                    «это надолго?». 12% от 200 МБ и 12% от 800 КБ — разные новости.
                    Моноширинный шрифт: число растёт, а строка не пляшет. */}
                {progress.read > 0 || progress.percent != null ? (
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--slate-300)', fontFeatureSettings: '"tnum" 1' }}>
                    {progress.percent != null ? progress.percent + '% · ' : ''}
                    {progress.total
                      ? `${fileSize(progress.read, lang)} ${t(lang, 'из', 'of')} ${fileSize(progress.total, lang)}`
                      : fileSize(progress.read, lang)}
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}
          {Array.from({ length: count || 1 }, (_, i) => i + 1).map((n) => (
            <div key={n} data-page={n}
                 ref={(el) => {
                   if (el) { slots.current.set(n, el); io.current?.observe(el) }
                   else slots.current.delete(n)
                 }}
                 style={{ position: 'relative', flex: '0 0 auto', width: w, height: w / (aspects[n] ?? aspect), background: '#fff', boxShadow: '0 8px 40px rgba(0,0,0,.4)' }}>
              {doc && near.includes(n) ? <PdfPageCanvas doc={doc} n={n} raster={raster} onAspect={onAspect} /> : null}
              {/* Слой разметки живёт вместе с холстом: смонтированный на все
                  триста листов, он клал вкладку ровно тем же, чем клали её
                  триста холстов, — только молча. */}
              {doc && near.includes(n) ? overlay(n) : null}
              {count > 1 ? (
                <span style={{ position: 'absolute', right: 8, bottom: 6, padding: '1px 6px', borderRadius: 3, background: 'rgba(15,23,42,.6)', color: '#fff', fontFamily: 'var(--font-mono)', fontSize: 10 }}>{n}</span>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Скачивание с долей выполненного.
 *
 * <p>Доля берётся из `Content-Length`. Его может не быть — за сжатым ответом
 * или за прокси, который его снял; тогда возвращается `null`, и интерфейс
 * говорит «идёт загрузка» вместо выдуманного процента. Полоса, врущая про
 * «87%», хуже отсутствия полосы: по ней принимают решение ждать.
 */
export async function download(
  url: string,
  onProgress: (progress: { percent: number | null; read: number; total: number | null }) => void,
): Promise<ArrayBuffer> {
  const res = await fetch(url)
  if (!res.ok) throw new Error('HTTP ' + res.status)
  const total = Number(res.headers.get('Content-Length') || 0)
  if (!res.body || !total) {
    /* Размер неизвестен — но байты всё равно считаются: «7,4 МБ» без процента
       уже говорит, идёт дело или встало. */
    onProgress({ percent: null, read: 0, total: null })
    const buffer = await res.arrayBuffer()
    onProgress({ percent: null, read: buffer.byteLength, total: null })
    return buffer
  }
  const reader = res.body.getReader()
  const chunks: Uint8Array[] = []
  let read = 0
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    if (value) {
      chunks.push(value)
      read += value.length
      onProgress({ percent: Math.min(99, Math.round((read / total) * 100)), read, total })
    }
  }
  const out = new Uint8Array(read)
  let at = 0
  for (const c of chunks) { out.set(c, at); at += c.length }
  return out.buffer
}

/**
 * Размер человеку. Килобайт — 1024: чертёж меряют тем же, чем его меряет
 * проводник, иначе «11,5 МБ» на экране и «11,0 МБ» в свойствах файла читаются
 * как разные файлы.
 */
export function fileSize(bytes: number, lang: Lang): string {
  if (!Number.isFinite(bytes) || bytes < 0) return ''
  const units = lang === 'ru' ? ['Б', 'КБ', 'МБ', 'ГБ'] : ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit++ }
  // Байты дробными не бывают; у остальных один знак — больше не читается.
  const shown = unit === 0 ? String(Math.round(value)) : value.toFixed(1)
  /* Пробел неразрывный и записан escape-последовательностью: обычный пробел
     позволил бы переносу оторвать «МБ» на другую строку, а невидимый U+00A0
     в исходнике читается как обычный и теряется при первой же правке. */
  return (lang === 'ru' ? shown.replace('.', ',') : shown) + '\u00A0' + units[unit]
}

/**
 * Вращающаяся иконка-лоадер тёмных плашек просмотрщика.
 *
 * <p>Вращение и reduced-motion-ветка (замедление до 2.4s, а не полная
 * скорость) — правило `.tdms-spin` в ui.css: оно одно на приложение,
 * локальная копия здесь однажды разошлась бы с эталоном.
 */
function SpinIcon({ size, style }: { size: number; style?: CSSProperties }) {
  return (
    <span className="tdms-spin" style={{ display: 'inline-flex', ...style }}>
      <Icon name="loader" size={size} />
    </span>
  )
}

/**
 * Файл едет.
 *
 * <p>Недействующая ревизия приходит не ссылкой на хранилище, а собранной
 * копией со штампом: её качает сам клиент, и на большом чертеже это секунды.
 * Раньше на этом месте стояло «Файл не загружен», то есть экран уверял, что
 * файла нет, — и через несколько секунд показывал его.
 */
function LoadingSheet({ lang }: { lang: Lang }) {
  return (
    <div style={{ position: 'absolute', inset: 0, background: 'var(--surface-sunken)', display: 'grid', placeItems: 'center', padding: 24 }}>
      <div style={{ maxWidth: 320, textAlign: 'center' }}>
        {/* Лоадер вертится: неподвижный он неотличим от зависания. */}
        <div style={{ display: 'inline-flex', marginBottom: 10, color: 'var(--text-subtle)' }}><SpinIcon size={30} /></div>
        <div style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)', marginBottom: 6 }}>
          {t(lang, 'Файл загружается…', 'Loading the file…')}
        </div>
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {t(lang, 'Копия собирается на сервере — это занимает несколько секунд.',
                   'The copy is being prepared on the server — this takes a few seconds.')}
        </div>
      </div>
    </div>
  )
}

/** Ни файла, ни выдумки: раньше на этом месте рисовался чертёж-заглушка. */
function NoSheet({ lang, rev, note }: { lang: Lang; rev: string | null; note?: string | null }) {
  return (
    <div style={{ position: 'absolute', inset: 0, background: 'var(--surface-sunken)', display: 'grid', placeItems: 'center', padding: 24 }}>
      <div style={{ maxWidth: 320, textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', marginBottom: 10, color: 'var(--text-subtle)' }}><Icon name="file-x" size={30} /></div>
        <div style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)', marginBottom: 6 }}>
          {note ? t(lang, 'Файл недоступен', 'File unavailable') : t(lang, 'Файл не загружен', 'No file uploaded')}
        </div>
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {note ?? (rev
            ? t(lang, `У ревизии ${rev} нет ни одной загруженной версии. Файл прикладывается в карточке документа.`,
                `Revision ${rev} has no uploaded version yet. Attach the file in the document card.`)
            : t(lang, 'У документа ещё нет ревизий.', 'The document has no revisions yet.'))}
        </div>
      </div>
    </div>
  )
}

/**
 * Не всякая версия — чертёж: в ревизию кладут и таблицы, и архивы. Раньше такой
 * файл всё равно уходил в pdf.js и экран сообщал «Не удалось отобразить PDF» —
 * то есть винил файл, хотя тот цел и просто другого типа.
 */
function OtherFileSheet({ url, name, lang, reason }: { url: string; name: string; lang: Lang
                                                      /** Почему показать нечем. Пусто — формат не поддержан вовсе. */
                                                      reason?: string | null }) {
  const ext = (name.split('.').pop() ?? '').toUpperCase()
  return (
    <div style={{ position: 'absolute', inset: 0, background: 'var(--surface-sunken)', display: 'grid', placeItems: 'center', padding: 24 }}>
      <div style={{ maxWidth: 340, textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', marginBottom: 10, color: 'var(--text-subtle)' }}><Icon name="file-text" size={30} /></div>
        <div style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)', marginBottom: 6 }}>
          {reason
            ? t(lang, 'Файл не удалось разобрать', 'The file could not be parsed')
            : t(lang, `Предпросмотр недоступен${ext ? ` для ${ext}` : ''}`, `No preview${ext ? ` for ${ext}` : ''}`)}
        </div>
        {/* Причина названа: пустой лист вместо файла читается как поломка
            системы, а «не разобралось, вот файл» — как то, что и произошло. */}
        {reason ? (
          <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 8 }}>{reason}</div>
        ) : null}
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 14, wordBreak: 'break-all' }}>{name}</div>
        <a href={url} download={name} style={{ textDecoration: 'none' }}>
          <Button variant="dark" size="sm" iconLeft={<Icon name="download" size={14} />}>{t(lang, 'Скачать', 'Download')}</Button>
        </a>
      </div>
    </div>
  )
}

/**
 * Книга Excel в просмотрщике.
 *
 * <p>Вспомогательный файл — расчёт, ведомость — раньше можно было только
 * скачать: заказчик открывал его в Excel рядом с чертежом, а на длинном круге
 * это десяток файлов на диске, каждый из которых потом не отличить от свежего.
 *
 * <p>Библиотека берётся ленивым импортом, как pdf.js: она уже стоит в сборке
 * ради выгрузок, но тянуть её в начальный кусок ради файла, который открывают
 * не каждый раз, незачем.
 *
 * <p>DOM собирается ЗДЕСЬ, из значений, а не вставкой готового HTML из
 * библиотеки: файл прислал подрядчик, и его разметке не место в чужом сеансе.
 * У формулы берётся посчитанное значение — то же правило, что и в разборе
 * бланков CRS на сервере.
 *
 * <p>Чего не будет: оформления, диаграмм и картинок. Это вспомогательный
 * просмотр, а не Excel; за точным видом — «Скачать».
 */
function SheetPreview({ url, name, lang }: { url: string; name: string; lang: Lang }) {
  const [book, setBook] = useState<{ names: string[]; rows: Record<string, string[][]> } | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const [active, setActive] = useState('')

  useEffect(() => {
    let cancelled = false
    setBook(null)
    setFailed(null)
    ;(async () => {
      try {
        const XLSX = await import('xlsx')
        const data = await (await fetch(url)).arrayBuffer()
        const wb = XLSX.read(data, { type: 'array' })
        const rows: Record<string, string[][]> = {}
        for (const sheetName of wb.SheetNames) {
          const ws = wb.Sheets[sheetName]
          /* header: 1 — строки массивами, а не объектами по первой строке:
             у ведомостей шапка бывает на третьей строке и с объединениями,
             и «первая строка это имена колонок» врёт чаще, чем помогает. */
          rows[sheetName] = ws
            ? (XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' }) as unknown[][])
                .map((r) => r.map((c) => (c == null ? '' : String(c))))
            : []
        }
        if (cancelled) return
        setBook({ names: wb.SheetNames, rows })
        setActive(wb.SheetNames[0] ?? '')
      } catch (e) {
        console.error('SheetPreview:', e)
        if (!cancelled) setFailed(e instanceof Error ? e.message : String(e))
      }
    })()
    return () => { cancelled = true }
  }, [url])

  if (failed) {
    return <OtherFileSheet url={url} name={name} lang={lang}
                           reason={t(lang, 'Не удалось разобрать как книгу Excel', 'Could not be parsed as an Excel workbook')} />
  }
  if (!book) {
    return (
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: 'var(--surface-sunken)' }}>
        {/* Спиннер к тексту: голая надпись неотличима от зависания. */}
        <span style={{ font: 'var(--font-ui)', color: 'var(--text-subtle)', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Spinner size={13} />
          {t(lang, 'Разбор книги…', 'Parsing the workbook…')}
        </span>
      </div>
    )
  }

  const rows = book.rows[active] ?? []
  const width = rows.reduce((w, r) => Math.max(w, r.length), 0)
  const cell: CSSProperties = {
    border: '1px solid var(--border-subtle)', padding: '3px 7px', whiteSpace: 'nowrap',
    font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', background: 'var(--surface-card)',
  }
  const head: CSSProperties = {
    ...cell, position: 'sticky', top: 0, zIndex: 1, background: 'var(--surface-sunken)',
    color: 'var(--text-subtle)', fontFamily: 'var(--font-mono)', fontSize: 11, textAlign: 'center',
  }

  return (
    /* Разобранная книга проявляется так же, как холст листа (см. PdfPageCanvas):
       мгновенная подмена лоадера содержимым читается как скачок. */
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', background: 'var(--surface-page)', animation: 'fade var(--duration-slow) var(--ease-out)' }}>
      {/* Листы книги: без переключателя виден только первый, а расчёт живёт на
          третьем. Одинокий лист полосой не отвлекает. */}
      {book.names.length > 1 ? (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '7px 12px', borderBottom: '1px solid var(--border-default)', background: 'var(--surface-card)', overflowX: 'auto' }}>
          {book.names.map((n) => (
            <button key={n} onClick={() => setActive(n)} className="tdms-rail"
                    style={{ border: '1px solid ' + (n === active ? 'var(--accent)' : 'var(--border-default)'),
                             background: n === active ? 'var(--accent-soft)' : 'transparent',
                             borderRadius: 'var(--radius-sm)', padding: '3px 10px', cursor: 'pointer', whiteSpace: 'nowrap',
                             font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{n}</button>
          ))}
        </div>
      ) : null}
      <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
        {width === 0 ? (
          <div style={{ font: 'var(--font-ui)', color: 'var(--text-subtle)' }}>{t(lang, 'Лист пуст', 'The sheet is empty')}</div>
        ) : (
          <table style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...head, left: 0, zIndex: 2 }} />
                {Array.from({ length: width }, (_, i) => <th key={i} style={head}>{columnName(i)}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>
                  {/* Номер строки — как в Excel, с единицы: по нему в переписке
                      и ссылаются («смотри строку 14»). */}
                  <th style={{ ...head, position: 'sticky', left: 0 }}>{ri + 1}</th>
                  {Array.from({ length: width }, (_, ci) => <td key={ci} style={cell}>{r[ci] ?? ''}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

/**
 * Документ Word в просмотрщике.
 *
 * <p>Пояснительная записка приходит вместе с чертежом, и до сих пор её можно
 * было только скачать. Библиотека берётся ленивым импортом: она нужна не
 * каждому открытию файла, и в начальном куске сборки ей не место.
 *
 * <p>На выходе у библиотеки HTML, и он идёт через {@code sanitizeHtml}, потому
 * что файл прислала ДРУГАЯ СТОРОНА. Обработчик события в атрибуте или
 * {@code javascript:} в ссылке выполнились бы в сеансе заказчика — в
 * документообороте это не выдуманная угроза, а обычный день: чужой файл
 * приходит каждый день и от организации, с которой идёт спор.
 *
 * <p>Старый двоичный {@code .doc} сюда не попадает вовсе: его не читает ни
 * одна браузерная библиотека, и обещать по нему предпросмотр нельзя
 * (см. previewKind).
 *
 * <p>Чего не будет: колонтитулов, сносок и точной вёрстки. За ними — «Скачать».
 */
function DocPreview({ url, name, lang }: { url: string; name: string; lang: Lang }) {
  const [html, setHtml] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setHtml(null)
    setFailed(false)
    ;(async () => {
      try {
        const mammoth = await import('mammoth/mammoth.browser')
        const data = await (await fetch(url)).arrayBuffer()
        const out = await mammoth.convertToHtml({ arrayBuffer: data })
        if (cancelled) return
        setHtml(sanitizeHtml(out.value))
      } catch (e) {
        console.error('DocPreview:', e)
        if (!cancelled) setFailed(true)
      }
    })()
    return () => { cancelled = true }
  }, [url])

  if (failed) {
    return <OtherFileSheet url={url} name={name} lang={lang}
                           reason={t(lang, 'Не удалось разобрать как документ Word', 'Could not be parsed as a Word document')} />
  }
  if (html == null) {
    return (
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: 'var(--surface-sunken)' }}>
        {/* Спиннер к тексту: голая надпись неотличима от зависания. */}
        <span style={{ font: 'var(--font-ui)', color: 'var(--text-subtle)', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Spinner size={13} />
          {t(lang, 'Разбор документа…', 'Parsing the document…')}
        </span>
      </div>
    )
  }

  return (
    /* Разобранный документ проявляется так же, как холст листа (PdfPageCanvas). */
    <div style={{ position: 'absolute', inset: 0, overflow: 'auto', background: 'var(--surface-sunken)', padding: '24px 0', animation: 'fade var(--duration-slow) var(--ease-out)' }}>
      {/* Лист по ширине текста, а не по ширине окна: записку читают, и строка
          в полтора экрана читается плохо. */}
      <div className="tdms-docx"
           style={{ maxWidth: 820, margin: '0 auto', padding: '32px 40px', background: 'var(--surface-card)',
                    border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)',
                    font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', lineHeight: 1.55 }}
           /* Разметка уже прошла чистку по белому списку строкой выше. Другого
              пути вставить разбранный документ нет: собирать его узлы вручную,
              как таблицу Excel, значит переписать половину библиотеки. */
           dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  )
}

/** Имя столбца по его номеру: 0 → A, 25 → Z, 26 → AA — как в самом Excel. */
function columnName(i: number): string {
  let n = i
  let out = ''
  do {
    out = String.fromCharCode(65 + (n % 26)) + out
    n = Math.floor(n / 26) - 1
  } while (n >= 0)
  return out
}

// ---- сравнение версий (попиксельные различия) --------------------------
let _pdfjs: PdfJs | null = null
async function getPdfjs(): Promise<PdfJs> {
  if (_pdfjs) return _pdfjs
  const pdfjs = (await import('pdfjs-dist')) as unknown as PdfJs
  _pdfjs = pdfjs
  /* Воркер поднимается сам, а не через workerSrc с адресом файла. Два повода.
     Первый: `new Worker(new URL(...))` — форма, которую Vite разбирает на
     сборке и кладёт воркер отдельным чанком .js; прежний путь через `?url`
     давал .mjs, расширение, которого nginx не знал, и браузеры успели
     закешировать ответ типа application/octet-stream на год с пометкой
     immutable. Отменить такую запись со стороны сервера нельзя — ключ кеша
     тот же, а immutable запрещает перепроверку. Второй: без воркера pdf.js
     разбирает документ в главном потоке, и на файле в сотню страниц это
     подвешивает вкладку. */
  pdfjs.GlobalWorkerOptions.workerPort = new Worker(
    new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url),
    { type: 'module' },
  )
  return pdfjs
}
/** Снимок листа и ЧИСЛО листов: в режиме сравнения PdfPages не смонтирован. */
interface Shot { canvas: HTMLCanvasElement; pages: number }

async function renderPdf(url: string, pageNo = 1, scale = 1.6): Promise<Shot> {
  const pdfjs = await getPdfjs()
  const data = await (await fetch(url)).arrayBuffer()
  const task = pdfjs.getDocument({ data })
  const pdf = await task.promise
  try {
    // У сравниваемых версий число страниц может отличаться — берём то, что есть.
    const page = await pdf.getPage(Math.min(Math.max(pageNo, 1), pdf.numPages))
    const viewport = page.getViewport({ scale })
    const canvas = document.createElement('canvas')
    canvas.width = viewport.width
    canvas.height = viewport.height
    await page.render({ canvas, viewport }).promise
    return { canvas, pages: pdf.numPages }
  } finally {
    /* Картинка уже снята — файл больше не нужен. Без этого каждое сравнение
       оставляло во вкладке две открытые версии навсегда. */
    void task.destroy()
  }
}
function imgData(c: HTMLCanvasElement, w: number, h: number): ImageData {
  if (c.width === w && c.height === h) return c.getContext('2d')!.getImageData(0, 0, w, h)
  const t2 = document.createElement('canvas')
  t2.width = w
  t2.height = h
  const tc = t2.getContext('2d')!
  tc.fillStyle = '#fff'
  tc.fillRect(0, 0, w, h)
  tc.drawImage(c, 0, 0)
  return tc.getImageData(0, 0, w, h)
}
// зелёное — линия есть только в B (добавлено), красное — только в A (удалено)
function makeDiff(cA: HTMLCanvasElement, cB: HTMLCanvasElement): { canvas: HTMLCanvasElement; added: number; removed: number } {
  const w = Math.max(cA.width, cB.width)
  const h = Math.max(cA.height, cB.height)
  const A = imgData(cA, w, h).data
  const B = imgData(cB, w, h).data
  const out = document.createElement('canvas')
  out.width = w
  out.height = h
  const oc = out.getContext('2d')!
  const O = oc.createImageData(w, h)
  const TH = 150
  let added = 0
  let removed = 0
  for (let i = 0; i < A.length; i += 4) {
    const gA = 0.299 * A[i]! + 0.587 * A[i + 1]! + 0.114 * A[i + 2]!
    const gB = 0.299 * B[i]! + 0.587 * B[i + 1]! + 0.114 * B[i + 2]!
    const onA = gA < TH
    const onB = gB < TH
    let r = 255
    let g = 255
    let b = 255
    if (onA && onB) { r = 70; g = 78; b = 90 } else if (onB && !onA) { r = 22; g = 150; b = 70; added++ } else if (onA && !onB) { r = 205; g = 70; b = 60; removed++ }
    O.data[i] = r
    O.data[i + 1] = g
    O.data[i + 2] = b
    O.data[i + 3] = 255
  }
  oc.putImageData(O, 0, 0)
  return { canvas: out, added, removed }
}

/**
 * Скачать лист с разметкой.
 *
 * <p>Разметку показывали только на экране: чтобы приложить обведённое место к
 * протоколу или к письму, её пересобирали снимком экрана. Берётся то, что
 * нарисовано, — холст листа и слой фигур поверх него, — и склеивается в PNG.
 *
 * <p>Работает по DOM, а не по данным: слой фигур уже собран и отмасштабирован
 * под лист, а вторая сборка тех же фигур в свою картинку означала бы, что
 * печать и экран рано или поздно разойдутся.
 */
async function exportSheetPng(page: number, name: string): Promise<void> {
  const slot = document.querySelector<HTMLElement>(`[data-page="${page}"]`)
  const canvas = slot?.querySelector('canvas')
  if (!slot || !canvas) throw new Error('лист ещё не отрисован')
  const out = document.createElement('canvas')
  out.width = canvas.width
  out.height = canvas.height
  const ctx = out.getContext('2d')
  if (!ctx) throw new Error('холст недоступен')
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, out.width, out.height)
  ctx.drawImage(canvas, 0, 0)

  const svg = slot.querySelector('svg')
  if (svg) {
    const copy = svg.cloneNode(true) as SVGSVGElement
    copy.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
    copy.setAttribute('width', String(out.width))
    copy.setAttribute('height', String(out.height))
    const markup = new XMLSerializer().serializeToString(copy)
    const url = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(markup)))
    await new Promise<void>((resolve, reject) => {
      const img = new Image()
      img.onload = () => { ctx.drawImage(img, 0, 0, out.width, out.height); resolve() }
      img.onerror = () => reject(new Error('слой разметки не отрисовался'))
      img.src = url
    })
  }
  const blob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, 'image/png'))
  if (!blob) throw new Error('картинка не собралась')
  const href = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = name
  a.click()
  URL.revokeObjectURL(href)
}

/**
 * Пара версий для сравнения.
 *
 * <p>Ревизия и версия названы отдельно от подписи: по ним замечание находит
 * СВОЮ сторону сравнения. Разбирать подпись обратно значило бы держать формат
 * строки контрактом.
 */
interface CompareItem {
  url: string
  name: string
  rev: string
  /** Версия ФАЙЛА: по ней собран адрес содержимого. */
  ver: number
  /**
   * Номер ПОДАЧИ этого набора; {@code null} — набор не отправляли.
   *
   * <p>Замечание привязано к ПОДАЧЕ (`document.ver`, счёт с нуля), а не к
   * версии файла (`doc_file.ver`, счёт с единицы и общий на основной файл,
   * исходники и вложения). Сравнивать одно с другим бесполезно: даже в
   * простейшем случае это «0» против «1». Перевод одного в другое умеет
   * только состав подач ревизии — см. submissionOf.
   */
  sub: number | null
}

function PdfCompare({ items, lang, page, onPages, onPage, onPair, overlay, leftShelf, shelf }: {
  items: CompareItem[]; lang: Lang; page: number
  /** Число листов: в сравнении PdfPages не смонтирован, и навигация в панели инструментов оставалась бы на «из 1». */
  onPages: (n: number) => void
  /** Перелистнуть на шаг: колесом в сравнении листают, а не прокручивают. */
  onPage?: (step: number) => void
  /** Какая пара сравнивается сейчас. Просмотрщику это нужно, чтобы взять слои
      разметки обеих сторон: разметка хранится по паре (ревизия, версия). */
  onPair?: (before: CompareItem, after: CompareItem) => void
  /**
   * Слой поверх стороны: метки прошлой подачи слева, рабочая разметка справа.
   *
   * <p>Рисует его ВЫЗЫВАЮЩИЙ тем же Canvas, что и обычный вид: вторая
   * реализация меток разошлась бы с первой на первой же правке, и разошлась бы
   * молча — метка встала бы не туда, где её поставили.
   *
   * <p>Только в режиме «Рядом». В «Наложении» и «Различиях» лист составной, и
   * метка на нём указывала бы на место, которого нет ни в одной из версий.
   */
  overlay?: (side: 'before' | 'after', item: CompareItem) => ReactNode
  /** Полка СЛЕВА: замечания прошлой подачи — то, что уже сказано. */
  leftShelf?: (before: CompareItem, after: CompareItem) => ReactNode
  /** Полка СПРАВА: выпуск нового замечания по версии, которую рассматривают. */
  shelf?: (before: CompareItem, after: CompareItem) => ReactNode
}) {
  const [aIdx, setAIdx] = useState(Math.min(1, items.length - 1))
  const [bIdx, setBIdx] = useState(0)
  const [mode, setMode] = useState('diff')
  const [busy, setBusy] = useState(true)
  const [err, setErr] = useState(false)
  /* Был ли хоть один успешный рендер. До него прятать нечего — холсты пустые;
     после — при пересчёте (перелистывание, смена пары) прежние холсты не
     гаснут display:none в тёмный провал, а приглушаются до готовности новых. */
  const [shown, setShown] = useState(false)
  /** Нет ли такого листа в стороне «Было» / «Стало». */
  const [missing, setMissing] = useState<[boolean, boolean]>([false, false])
  /* Масштаб ОБЩИЙ на две стороны: сравнивают одно и то же место, и разъехавшись
     в размере, стороны перестают сравниваться вовсе. */
  const [zoom, setZoom] = useState(1)
  const boxRef = useRef<HTMLDivElement>(null)
  const aRef = useRef<HTMLCanvasElement>(null)
  const bRef = useRef<HTMLCanvasElement>(null)
  const ovaRef = useRef<HTMLCanvasElement>(null)
  const ovbRef = useRef<HTMLCanvasElement>(null)
  const diffRef = useRef<HTMLCanvasElement>(null)
  const urlA = items[aIdx]?.url
  const urlB = items[bIdx]?.url
  useEffect(() => {
    if (!urlA || !urlB) return
    let cancelled = false
    setBusy(true)
    setErr(false)
    ;(async () => {
      try {
        const [shotA, shotB] = await Promise.all([renderPdf(urlA, page), renderPdf(urlB, page)])
        if (cancelled) return
        const cA = shotA.canvas
        const cB = shotB.canvas
        /* Листов столько, сколько их в БОЛЬШЕЙ версии: переиздание добавляет
           листы, и по меньшей из двух до новых было бы не дойти. */
        onPages(Math.max(shotA.pages, shotB.pages))
        /* Отсутствующий лист называется, а не подменяется. renderPdf зажимает
           номер по числу листов версии — значит на листе 8 короткая версия
           молча отдавала свой последний, третий, и различия рисовались
           честными цветами по разным листам. Такое сравнение хуже отказа: оно
           выглядит достоверным. */
        setMissing([page > shotA.pages, page > shotB.pages])
        const blit = (src: HTMLCanvasElement, dst: HTMLCanvasElement | null) => {
          if (!dst) return
          dst.width = src.width
          dst.height = src.height
          dst.getContext('2d')!.drawImage(src, 0, 0)
        }
        blit(cA, aRef.current)
        blit(cA, ovaRef.current)
        blit(cB, bRef.current)
        blit(cB, ovbRef.current)
        blit(makeDiff(cA, cB).canvas, diffRef.current)
        setBusy(false)
        setShown(true)
      } catch (e) {
        console.error('PdfCompare:', e)
        if (!cancelled) { setErr(true); setBusy(false) }
      }
    })()
    return () => { cancelled = true }
  }, [urlA, urlB, page, onPages])

  /* Колесо листает, Ctrl с колесом — увеличивает. Тот же жест, что в обычном
     виде, и та же причина слушать событие вручную: браузер не даёт отменить
     прокрутку страницы из пассивного обработчика, а без отмены под пальцем
     уезжает вся страница.

     Листает ВВЕРХ-ВНИЗ по листам, а не прокручивает: в сравнении лист один на
     сторону и целиком помещается в окно, прокручивать нечего. */
  useEffect(() => {
    const root = boxRef.current
    if (!root) return
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
        setZoom((z) => Math.min(4, Math.max(0.5, z * (e.deltaY < 0 ? 1.12 : 1 / 1.12))))
        return
      }
      if (!onPage) return
      e.preventDefault()
      onPage(e.deltaY > 0 ? 1 : -1)
    }
    root.addEventListener('wheel', onWheel, { passive: false })
    return () => root.removeEventListener('wheel', onWheel)
  }, [onPage])
  /* Увеличение — множителем к предельной высоте, одинаковым для обеих сторон:
     холсты отрисованы одним масштабом, и общий предел держит их в размер. */
  const cstyle: CSSProperties = { maxWidth: zoom > 1 ? 'none' : '100%', maxHeight: `${62 * zoom}vh`,
                                  background: '#fff', boxShadow: '0 6px 30px rgba(0,0,0,.4)', display: 'block' }
  const labelStyle: CSSProperties = { textAlign: 'center', marginTop: 8, color: 'var(--slate-300)', font: 'var(--font-ui)', fontSize: 12 }
  const sel = (val: number, set: (n: number) => void, p: string) => (
    <Select size="sm" value={String(val)} onChange={(e) => set(+e.target.value)} block={false} style={{ maxWidth: 260 }}>{items.map((it, i) => <option key={i} value={i}>{p}: {it.name}</option>)}</Select>
  )
  /* Предупреждение о подменённом листе. Молчать нельзя: цвета различий на
     разных листах выглядят так же убедительно, как на одинаковых. */
  const noSheet = (side: 0 | 1) => missing[side]
    ? <span style={{ display: 'block', color: 'var(--amber-400)', font: 'var(--font-caption)' }}>
        {t(lang, `листа ${page} в этой версии нет — показан последний`,
                 `sheet ${page} is absent here — showing the last one`)}
      </span>
    : null
  const swatch = (c: string, txt: string) => <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: c }} />{txt}</span>
  const before = items[aIdx]
  const after = items[bIdx]
  /* Пара сообщается наверх, а не вычисляется там заново: выбор живёт здесь, и
     считать его дважды значит однажды разойтись. */
  useEffect(() => {
    if (before && after) onPair?.(before, after)
  }, [before, after, onPair])
  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex' }}>
    {/* Полка слева — про то, что БЫЛО сказано; справа — про то, что говорят
        СЕЙЧАС. Стороны листов между ними в том же порядке: глаз не должен
        перепрыгивать через экран, сверяя замечание с местом. */}
    {leftShelf && before && after ? leftShelf(before, after) : null}
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', background: 'var(--surface-card)', borderBottom: '1px solid var(--border-default)', flexWrap: 'wrap' }}>
        {sel(aIdx, setAIdx, t(lang, 'Было', 'Before'))}
        <span style={{ color: 'var(--text-subtle)', display: 'inline-flex' }}><Icon name="arrows-right-left" size={16} /></span>
        {sel(bIdx, setBIdx, t(lang, 'Стало', 'After'))}
        <div style={{ flex: 1 }} />
        {mode === 'diff' ? <div style={{ display: 'flex', alignItems: 'center', gap: 12, font: 'var(--font-caption)', color: 'var(--text-muted)' }}>{swatch('rgb(22,150,70)', t(lang, 'добавлено', 'added'))}{swatch('rgb(205,70,60)', t(lang, 'удалено', 'removed'))}{swatch('rgb(70,78,90)', t(lang, 'без изм.', 'same'))}</div> : null}
        <Segmented size="sm" value={mode} onChange={setMode} options={[{ value: 'side', label: t(lang, 'Рядом', 'Side') }, { value: 'overlay', label: t(lang, 'Наложение', 'Overlay') }, { value: 'diff', label: t(lang, 'Различия', 'Diff') }]} />
      </div>
      <div ref={boxRef} style={{ flex: 1, overflow: 'auto', background: 'var(--slate-800)', display: 'grid', placeItems: 'center', padding: 22, position: 'relative' }}>
        {/* Первый рендер — индикатор по центру пустого поля; пересчёт — плашкой
            сверху, поверх приглушённых прежних холстов: они остаются на месте. */}
        {busy ? (
          <span style={{ ...(shown ? { position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 1 } : {}),
                         display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--slate-300)', font: 'var(--font-ui)', whiteSpace: 'nowrap' }}>
            <SpinIcon size={14} style={{ color: 'var(--amber-300)' }} />
            {t(lang, 'Рендеринг и сравнение…', 'Rendering & diffing…')}
          </span>
        ) : null}
        {err ? <span style={{ color: 'var(--slate-300)', fontFamily: 'monospace' }}>{t(lang, 'Не удалось сравнить PDF', 'Compare failed')}</span> : null}
        {!busy && !err && mode !== 'side' && (missing[0] || missing[1]) ? (
          <div style={{ position: 'absolute', top: 8, color: 'var(--amber-400)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)' }}>
            {t(lang, `Листа ${page} нет в одной из версий — различия считаются по разным листам`,
                     `Sheet ${page} is absent in one version — the diff is across different sheets`)}
          </div>
        ) : null}
        {/* Пересчёт (busy при shown) не прячет холсты, а приглушает: перелистывание
            без провала в пустой тёмный экран. display-свап по режимам остаётся —
            частое переключение видов осознанно мгновенное. */}
        <div style={{ display: shown && !err && mode === 'side' ? 'flex' : 'none', gap: 18, alignItems: 'flex-start', opacity: busy ? 0.5 : 1, transition: 'opacity var(--duration-fast) var(--ease-standard)' }}>
          {/* Слой ложится НА ХОЛСТ, и обёртка обязана быть ровно по холсту.
              Canvas — это absolute/inset:0, он натягивается на весь
              позиционированный предок: попади в ту же обёртку подпись листа,
              слой стал бы выше холста на её высоту, и доля 1.0 рисовалась бы
              ниже кромки листа. Хуже показа — постановка: булавка сохранилась
              бы как y/(H+подпись) и в обычном виде встала бы не туда, куда её
              ставили. Поэтому подпись — СНАРУЖИ, следующим элементом.

              Координаты хранятся долями листа, и viewBox растягивается сам —
              пересчёта под масштаб сравнения не нужно. */}
          <div>
            <div style={{ position: 'relative', display: 'flex' }}>
              <canvas ref={aRef} style={cstyle} />
              {before && overlay ? overlay('before', before) : null}
            </div>
            <div style={labelStyle}>{items[aIdx]?.name}{noSheet(0)}</div>
          </div>
          <div>
            <div style={{ position: 'relative', display: 'flex' }}>
              <canvas ref={bRef} style={cstyle} />
              {after && overlay ? overlay('after', after) : null}
            </div>
            <div style={labelStyle}>{items[bIdx]?.name}{noSheet(1)}</div>
          </div>
        </div>
        <div style={{ display: shown && !err && mode === 'overlay' ? 'block' : 'none', position: 'relative', opacity: busy ? 0.5 : 1, transition: 'opacity var(--duration-fast) var(--ease-standard)' }}>
          <canvas ref={ovaRef} style={cstyle} />
          <canvas ref={ovbRef} style={{ ...cstyle, position: 'absolute', left: 0, top: 0, mixBlendMode: 'difference', boxShadow: 'none' }} />
        </div>
        <div style={{ display: shown && !err && mode === 'diff' ? 'block' : 'none', opacity: busy ? 0.5 : 1, transition: 'opacity var(--duration-fast) var(--ease-standard)' }}>
          <canvas ref={diffRef} style={cstyle} />
        </div>
      </div>
    </div>
    {shelf && before && after ? shelf(before, after) : null}
    </div>
  )
}

// ---- слой разметки -----------------------------------------------------
function Canvas({
  tool,
  stamp,
  pieces,
  onDraw,
  marks,
  draftPin,
  addPin,
  readOnly = false,
  showPins = true,
  showShapes = true,
  picked = null,
  onPick,
  focusId = null,
}: {
  tool: string
  stamp: string
  pieces: Piece[]
  onDraw: (s: Shape) => void
  marks: Mark[]
  draftPin: Pt | null
  addPin: (p: Pt) => void
  readOnly?: boolean
  showPins?: boolean
  showShapes?: boolean
  /** Выбранная фигура: её снимают кнопкой или клавишей Delete. */
  picked?: string | null
  onPick?: (id: string | null) => void
  /** Метка, к которой перешли из списка: её надо найти глазами на листе. */
  focusId?: string | null
}) {
  const svgRef = useRef<SVGSVGElement>(null)
  const draft = useRef<Shape | null>(null)
  const [, force] = useState(0)
  function pt(e: React.PointerEvent): Pt {
    const s = svgRef.current!
    const p = s.createSVGPoint()
    p.x = e.clientX
    p.y = e.clientY
    const m = p.matrixTransform(s.getScreenCTM()!.inverse())
    return { x: m.x, y: m.y }
  }
  function down(e: React.PointerEvent) {
    if (tool === 'cursor') { onPick?.(null); return }
    if (readOnly) return
    const p = pt(e)
    if (tool === 'pin') { addPin(p); return }
    if (tool === 'stamp') { onDraw({ t: 'stamp', x: p.x, y: p.y, label: stamp }); return }
    if (tool === 'text') { onDraw({ t: 'text', x: p.x, y: p.y, label: 'Примечание' }); return }
    ;(e.target as Element).setPointerCapture(e.pointerId)
    if (tool === 'pen') draft.current = { t: 'pen', pts: [p] }
    else draft.current = { t: tool, x0: p.x, y0: p.y, x1: p.x, y1: p.y }
    force((n) => n + 1)
  }
  function move(e: React.PointerEvent) {
    if (!draft.current) return
    const p = pt(e)
    if (draft.current.t === 'pen') draft.current.pts!.push(p)
    else { draft.current.x1 = p.x; draft.current.y1 = p.y }
    force((n) => n + 1)
  }
  /* Фигура сохраняется, как только жест закончен: без этого разметка живёт
     до F5, ради чего её и хранить незачем. */
  function up() {
    if (!draft.current) return
    onDraw(draft.current)
    draft.current = null
    force((n) => n + 1)
  }
  const all: Piece[] = draft.current
    ? [...pieces, { id: '', s: draft.current, crs: null, mine: true }]
    : pieces
  return (
    /* preserveAspectRatio="none" обязателен: viewBox растягивается на лист, и
       только тогда x/VB_W — действительно доля ширины страницы. По умолчанию
       он вписывается с полями, и на листе, чьи пропорции отличны от 1000×707
       (титул A4 в альбоме A1, скан с полями), разметка съезжала и уменьшалась
       вместе с полями — в выданном файле она стояла не там, где её ставили. */
    <svg ref={svgRef} viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio="none"
      onPointerDown={down} onPointerMove={move} onPointerUp={up}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', cursor: readOnly || tool === 'cursor' ? 'default' : 'crosshair', touchAction: 'none' }}>
      {/* Вход кольца найденной метки: кольцо монтируется по смене focusId, и
          keyframe проигрывается один раз. Правило на корне svg — <style> внутри
          <g> спецификация не обещает. */}
      <style>{'.tdms-ring-in { transform-box: fill-box; transform-origin: center; animation: tdms-ring-in var(--duration-normal) var(--ease-out); } @keyframes tdms-ring-in { from { transform: scale(1.3); opacity: 0; } to { transform: scale(1); opacity: 0.9; } }'}</style>
      {showShapes ? all.map((p, i) => (
        <g key={p.id || 'draft-' + i}
           onPointerDown={(e) => { if (tool === 'cursor' && p.id) { e.stopPropagation(); onPick?.(p.id) } }}
           style={{ cursor: tool === 'cursor' && p.id ? 'pointer' : undefined }}>
          <Shape s={p.s} tone={p.stale ? 'stale' : p.crs ? 'crs' : 'note'} picked={!!p.id && p.id === picked} />
        </g>
      )) : null}
      {/* Булавка рисуется булавкой, а замечание на обводке — номерком у её угла:
          рисовать поверх облака ещё одну булавку значило бы прятать саму обводку. */}
      {showPins ? marks.map((m) => (
        <g key={m.id} transform={`translate(${m.x},${m.y})`} style={{ cursor: 'pointer' }}>
          {/* Кольцо у той метки, к которой только что перешли: без него на
              плотном чертеже её ищешь глазами столько же, сколько искал бы без
              перехода. Вход сжатием: прыжок прокрутки мгновенный намеренно, и
              после него движение кольца — единственное, что ведёт глаз к месту.
              transform-box: fill-box — иначе scale считался бы от угла листа.
              Длительность из токена — гаснет при «меньше движения». */}
          {m.id === focusId ? (
            <circle className="tdms-ring-in" cx="0" cy={m.kind === 'pin' ? -19 : 0} r="17" fill="none"
                    stroke="var(--accent)" strokeWidth="3" opacity="0.9" />
          ) : null}
          {m.kind === 'pin' ? (
            <>
              <path d="M0 0 C -11 -16 -11 -30 0 -30 C 11 -30 11 -16 0 0 Z" transform="translate(0,2)" fill="var(--code-3)" stroke="#fff" strokeWidth="1.5" />
              <circle cx="0" cy="-19" r="9" fill="#fff" />
              <text x="0" y="-15" textAnchor="middle" fontSize="11" fontWeight="700" fill="var(--code-3)" fontFamily="monospace">{m.n}</text>
            </>
          ) : (
            <>
              <circle cx="0" cy="0" r="10" fill="var(--code-3)" stroke="#fff" strokeWidth="1.5" />
              <text x="0" y="4" textAnchor="middle" fontSize="11" fontWeight="700" fill="#fff" fontFamily="monospace">{m.n}</text>
            </>
          )}
        </g>
      )) : null}
      {draftPin ? (
        <g transform={`translate(${draftPin.x},${draftPin.y})`}>
          <path d="M0 0 C -11 -16 -11 -30 0 -30 C 11 -30 11 -16 0 0 Z" transform="translate(0,2)" fill="none" stroke="var(--code-3)" strokeWidth="2" strokeDasharray="4 3" />
        </g>
      ) : null}
    </svg>
  )
}

/**
 * Фигура на листе.
 *
 * <p>Цвет говорит о происхождении: красным — то, что несёт замечание, серым
 * пунктиром — рабочая пометка, сделанная вне рассмотрения. Одинаковый вид у
 * того и другого приводил к тому, что чужой карандаш принимали за официальное
 * замечание, а на капитальном проекте это спор о деньгах.
 */
function Shape({ s, tone = 'crs', picked = false }: { s: Shape; tone?: 'crs' | 'note' | 'stale'; picked?: boolean }) {
  /* Снятое или закрытое замечание — бледная обводка: она осталась историей
     листа, но действующей её показывать нельзя. */
  const red = tone === 'crs' ? '#cf4d3f' : tone === 'stale' ? 'var(--slate-400)' : 'var(--slate-500)'
  const dash = tone === 'note' ? '7 5' : tone === 'stale' ? '3 6' : undefined
  const glow = picked
    ? { filter: 'drop-shadow(0 0 0 var(--accent))', outline: 'none' }
    : undefined
  if (picked) {
    /* Выбранная обводится сама собой поверх: рамку по границам фигуры пришлось
       бы считать для каждого вида отдельно, а вторая копия той же геометрии
       толстым полупрозрачным штрихом читается однозначно. */
    return (
      <g>
        <g opacity={0.35} style={{ pointerEvents: 'none' }}>
          <ShapeBody s={s} color="var(--accent)" width={9} />
        </g>
        <ShapeBody s={s} color={red} dash={dash} />
      </g>
    )
  }
  return <ShapeBody s={s} color={red} dash={dash} style={glow} />
}

/** Геометрия фигуры без решений о цвете: их принимает вызывающий. */
function ShapeBody({ s, color, width, dash, style }: {
  s: Shape
  color: string
  width?: number
  dash?: string
  style?: CSSProperties
}) {
  const red = color
  const sw = String(width ?? 2.5)
  if (s.t === 'pen') return <polyline points={(s.pts ?? []).map((p) => p.x + ',' + p.y).join(' ')} fill="none" stroke={red} strokeWidth={sw} strokeDasharray={dash} style={style} strokeLinecap="round" strokeLinejoin="round" />
  if (s.t === 'rect') return <rect x={Math.min(s.x0!, s.x1!)} y={Math.min(s.y0!, s.y1!)} width={Math.abs(s.x1! - s.x0!)} height={Math.abs(s.y1! - s.y0!)} fill="none" stroke={red} strokeWidth={sw} strokeDasharray={dash} style={style} />
  if (s.t === 'cloud') {
    const x = Math.min(s.x0!, s.x1!), y = Math.min(s.y0!, s.y1!), w = Math.abs(s.x1! - s.x0!), h = Math.abs(s.y1! - s.y0!)
    const r = 11
    let d = `M ${x} ${y + r}`
    for (let i = 0; i < Math.max(1, Math.round(w / (r * 2))); i++) d += ` a ${r} ${r} 0 0 1 ${r * 2} 0`
    for (let i = 0; i < Math.max(1, Math.round(h / (r * 2))); i++) d += ` a ${r} ${r} 0 0 1 0 ${r * 2}`
    for (let i = 0; i < Math.max(1, Math.round(w / (r * 2))); i++) d += ` a ${r} ${r} 0 0 1 ${-r * 2} 0`
    for (let i = 0; i < Math.max(1, Math.round(h / (r * 2))); i++) d += ` a ${r} ${r} 0 0 1 0 ${-r * 2}`
    return <path d={d} fill="none" stroke={red} strokeWidth={sw} strokeDasharray={dash} style={style} />
  }
  if (s.t === 'arrow') {
    const ang = Math.atan2(s.y1! - s.y0!, s.x1! - s.x0!)
    const ah = 14
    return <g stroke={red} strokeWidth={sw} strokeDasharray={dash} style={style} fill="none"><line x1={s.x0} y1={s.y0} x2={s.x1} y2={s.y1} /><path d={`M ${s.x1} ${s.y1} L ${s.x1! - ah * Math.cos(ang - 0.4)} ${s.y1! - ah * Math.sin(ang - 0.4)} M ${s.x1} ${s.y1} L ${s.x1! - ah * Math.cos(ang + 0.4)} ${s.y1! - ah * Math.sin(ang + 0.4)}`} /></g>
  }
  if (s.t === 'text') return <g><rect x={s.x! - 4} y={s.y! - 14} width={(s.label ?? '').length * 7.5 + 8} height={20} fill="#fff" stroke={red} strokeWidth="1" /><text x={s.x} y={s.y} fontSize="13" fill={red} fontFamily="sans-serif">{s.label}</text></g>
  if (s.t === 'stamp') {
    const col = ({ AFD: '#3f9152', IFC: '#4d5a6a', REVISE: '#d6a318', VOID: '#cf4d3f' } as Record<string, string>)[s.label!] || red
    return <g transform={`translate(${s.x},${s.y}) rotate(-8)`}><rect x="-46" y="-18" width="92" height="36" fill="none" stroke={col} strokeWidth="3" rx="3" /><text x="0" y="6" textAnchor="middle" fontSize="18" fontWeight="800" fill={col} fontFamily="sans-serif" letterSpacing="1">{s.label}</text></g>
  }
  return null
}

// ---- вьювер ------------------------------------------------------------
export function DocumentViewer({ no, crs, page: openAt, rev: openRev, ver: openVer, lang = 'ru', onBack }: {
  no: string
  crs?: string
  /** Лист, на который надо открыться: так приходят находки поиска по файлам. */
  page?: number
  /** Ревизия и версия находки: текст нашёлся в конкретном файле, а не в последнем. */
  rev?: string
  ver?: number
  lang?: Lang
  onBack: () => void
}) {
  /* Документ лежит ровно в одном из двух реестров, и в каком — на входе
     неизвестно: реестр поставщика спрашивается, только если в реестре
     заказчика документа нет. */
  const docQuery = useItem<Doc>('docs', no)
  const vdocQuery = useItem<Vdoc>('vdocs', docQuery.isError ? no : null)
  const doc = docQuery.data ?? vdocQuery.data

  const base = `/docs/${encodeURIComponent(no)}`
  const revisionsQuery = useSub<Revision[]>(doc ? base + '/revisions' : null)
  const revisions = useMemo(() => revisionsQuery.data ?? [], [revisionsQuery.data])

  /* История ревизий заполнена не у всех документов: в реестре ревизия есть
     («рев. A»), а строк в истории может не быть вовсе. Берём то, что реестр
     знает наверняка, вместо пустого выбора — придумывать даты и авторов
     несуществующих выпусков нельзя. */
  const revList = useMemo(() => {
    /* Номера без повторов. Один и тот же номер приходит дважды — повторной
       подачей после возврата, — и переключатель рисовал два одинаковых чипа
       «рев. 00»: React ругался на повторяющиеся ключи, а нажатие на любой из
       них открывало одно и то же. Файлы адресуются НОМЕРОМ ревизии, так что
       различать подачи здесь нечем и незачем: они в хронологии карточки. */
    if (revisions.length) return [...new Set(revisions.map((r) => r.rev))]
    const current = doc?.rev
    return current && current !== '—' ? [current] : []
  }, [revisions, doc])
  const [pickedRev, setPickedRev] = useState<string | null>(null)
  const rev = pickedRev ?? revList[0] ?? null

  const versionsQuery = useSub<DocVersion[]>(rev ? `${base}/versions/${encodeURIComponent(rev)}` : null)
  const versions = useMemo(() => versionsQuery.data ?? [], [versionsQuery.data])
  const [pickedVer, setPickedVer] = useState<number | null>(null)
  /* Поиск по тексту открытого документа: чертёж на триста листов листали
     глазами — общий поиск отвечает «нашлось в этом альбоме», а внутри файла
     искать было нечем. Ищется по тому же текстовому слою, что и общий поиск. */
  const [needle, setNeedle] = useState('')
  const [hits, setHits] = useState<{ page: number; snippet: string; ocr: boolean }[] | null>(null)
  const [searching, setSearching] = useState(false)
  /* Версия — номер ОСНОВНОГО файла, а не последняя загруженная строка.
     Счётчик версий общий на ревизию, и его тратит каждая роль: приложите к
     чертежу исходник — и просмотрщик открывал dwg вместо чертежа, потому что
     его номер больше. Та же группировка уже написана для карточки. */
  const mainVersions = useMemo(
    () => versions.filter((v) => (v.kind ?? 'main') === 'main'),
    [versions],
  )
  const shown = mainVersions.length ? mainVersions : versions
  const ver = pickedVer ?? (shown.length ? Math.max(...shown.map((v) => v.v)) : null)

  /* Тип версии определяется по имени файла: карточка документа его уже отдаёт,
     и ради того же ответа не стоит менять контракт. Пока имя неизвестно,
     считаем файл чертежом — так вело себя и прежнее поведение. */
  const fileName = versions.find((v) => v.v === ver)?.file ?? null
  /* Вид файла решает, чем его показывать. Разметка, замечания и сравнение
     по-прежнему только у PDF: замечание привязано к листу и координатам, а у
     таблицы и записки ни листов, ни координат. Excel и Word — вспомогательные,
     и нужда у них одна: посмотреть, не скачивая. */
  const kind = previewKind(fileName)
  const isPdf = kind === 'pdf'

  const file = useFileUrl(no, rev, ver)
  const crsQuery = useList<Crs>('crs', { page: 0, pageSize: 200, filters: { doc: [no] }, sort: [{ id: 'id', desc: false }] })
  const markupPath = rev && ver ? `${base}/revisions/${encodeURIComponent(rev)}/versions/${ver}/markup` : null
  const markupQuery = useSub<StoredMarkup[]>(markupPath)

  const me = useMe().data
  const [variant, setVariant] = useState('classic')
  const [tool, setTool] = useState('pin')
  /* Слой вспомогательной графики: рисуют и вне рассмотрения — для себя или
     сразу для второй стороны. Замечание живёт по своим правилам, а обводка,
     стрелка и штамп — нет, и кому они видны, решает рисующий. */
  const [share, setShare] = useState(false)
  const [stamp, setStamp] = useState('REVISE')
  const [scale, setScale] = useState(1)
  /* Страница листа. Разметка привязана к ней (колонка page в markup), поэтому
     номер обязан быть настоящим, а не единицей на все случаи. */
  const [page, setPage] = useState(1)
  const [pageCount, setPageCount] = useState(1)
  /* Загрузка файла — состояние, о котором обязан говорить и лист, и тулбар:
     чертёж на два десятка мегабайт идёт секундами, и пустой лист всё это время
     читался как «файла нет». Колбэк стабилен (useCallback), иначе эффект
     загрузки перезапускался бы на каждый свой же setState. */
  const [pdfState, setPdfState] = useState<PdfLoadState>(
    { done: false, percent: 0, read: 0, total: null, failed: false })
  const onPdfLoading = useCallback((s: PdfLoadState) => { setPdfState(s) }, [])
  /* Прокрутка к странице запрашивается счётчиком, а не самим номером: повторный
     переход к той же странице обязан сработать, а номер при этом не меняется. */
  const [goto, setGoto] = useState({ page: 1, nonce: 0 })
  const [pageDraft, setPageDraft] = useState('1')
  const pageInputRef = useRef<HTMLInputElement>(null)
  /* Булавка помнит свою страницу: замечание выпускается из правой полки, когда
     на экране может быть уже соседний лист. */
  const [draftPin, setDraftPin] = useState<(Pt & { page: number }) | null>(null)
  /* Нарисованная фигура — такое же отмеченное место, как булавка. Обводят как
     раз для того, чтобы сказать про обведённое, поэтому замечание должно
     ложиться на неё, а не требовать сверху ещё одной булавки. */
  const [draftMark, setDraftMark] = useState<{ id: string; page: number; kind: string } | null>(null)
  const [draftText, setDraftText] = useState('')
  const [draftCrit, setDraftCrit] = useState('med')

  const invalidateMarkup = () => (markupPath ? [qk.sub(markupPath)] : [])
  /* Страница приходит от того листа, на котором вели указателем, а не от той,
     что считается текущей: у границы прокрутки это разные страницы. */
  const drawShape = useCommand<{ shape: Shape; page: number }, { id: string }>({
    path: () => markupPath!,
    body: (a) => ({ page: a.page, kind: a.shape.t, geom: toGeom(a.shape), label: a.shape.label ?? null,
                    visibility: share ? 'shared' : 'internal' }),
    invalidate: () => [...invalidateMarkup(), qk.all('audit')],
  })
  const eraseShape = useCommand<{ id: string }>({
    method: 'DELETE',
    path: (a) => `${markupPath}/${a.id}`,
    invalidate: () => [...invalidateMarkup(), qk.all('audit')],
  })
  /* Замечание и булавка ставятся вместе: булавка без замечания — точка без
     смысла, а замечание по разметке без места на листе не найти. */
  /* В каком рассмотрении вошедший вправе высказаться по этому документу.
     Номер спрашивается у сервера, а не выводится экраном: замечание принадлежит
     рассмотрению, и угадывать его принадлежность клиенту нечем. */
  const myReview = useSub<{ review: { trm: string; step: string; act: string } | null }>(`/review/for-doc/${encodeURIComponent(no)}`)
  const reviewTrm = myReview.data?.review?.trm ?? null
  /* Акт шага, а не просто «есть пакет». Замечания выпускают рецензент,
     входной контроль и — с открытой подписью — утверждающий: сводя чужие,
     он вправе добавить своё. Правка и решения остаются только за ним. */
  const myAct = myReview.data?.review?.act ?? null
  const canRaise = myAct === 'check' || myAct === 'review' || myAct === 'approve'
  const iApproveHere = myAct === 'approve'

  /* Закрыть замечание. Закрывает тот, кто поставил, или утверждающий — это
     проверяет сервер; экран лишь не показывает кнопку заведомо чужому. */
  const closeCrs = useCommand<{ crs: string; decision: string | null }>({
    path: (a) => `/crs/${encodeURIComponent(a.crs)}/close`,
    body: (a) => ({ finalDecision: a.decision }),
    invalidate: () => [qk.all('crs'), qk.all('crs-sheets'), qk.all('docs'), qk.all('vdocs'),
                       qk.all('audit'), ...inboxKeys],
  })
  /* Правка формулировок ПАЧКОЙ: у утверждающего это одна работа — свести
     замечания рецензентов в один лист, а не десять отдельных сохранений. */
  const editAll = useCommand<{ items: { crs: string; body: string; crit: string; clause: string | null }[] }>({
    path: () => `/transmittals/${encodeURIComponent(reviewTrm ?? '')}/review/comments`,
    method: 'PUT',
    body: (a) => ({ items: a.items }),
    invalidate: () => [qk.all('crs'), qk.all('audit'),
                       qk.sub(`/transmittals/${encodeURIComponent(reviewTrm ?? '')}/review`), ...inboxKeys],
  })

  /* Замечание выпускается внутри рассмотрения и никак иначе: каждое замечание
     обязано лечь в лист CRS, а лист собирается по итогам рассмотрения. Кода
     рассмотрения у замечания нет — код у документа, его ставит утверждающий. */
  const raiseCrs = useCommand<{ text: string; crit: string; page: number }, { raised: string }>({
    path: () => `/transmittals/${encodeURIComponent(reviewTrm ?? '')}/review/comments`,
    body: (a) => ({ docNo: no, page: a.page, crit: a.crit, clause: `по разметке листа ${a.page}`, body: a.text }),
    invalidate: () => [qk.all('crs'), qk.all('docs'), qk.all('vdocs'), qk.all('audit'), ...inboxKeys,
                       qk.sub(`/transmittals/${encodeURIComponent(reviewTrm ?? '')}/review`)],
  })
  const pinCrs = useCommand<{ at: Pt & { page: number }; crs: string }>({
    path: () => markupPath!,
    body: (a) => ({ page: a.at.page, kind: 'pin', geom: toGeom({ t: 'pin', x: a.at.x, y: a.at.y }), crs: a.crs }),
    invalidate: () => invalidateMarkup(),
  })
  /* Перенос обводок прошлой ревизии. Второй круг рассмотрения — это проверка
     «устранено ли то, что мы обвели»: до сих пор новый лист открывался чистым,
     и рецензент искал свои прошлые места глазами по списку CRS. Что переносить,
     решает сервер: закрытое замечание с повестки снято. */
  const carryOver = useCommand<void, { from: string; carried: number }>({
    path: () => `${markupPath}/carry-over`,
    invalidate: () => [...invalidateMarkup(), qk.all('audit')],
  })
  /* Фигура уже лежит в базе — ей проставляется код замечания. Отдельная команда,
     потому что рисуют раньше, чем формулируют: сначала обводят место. */
  const attachCrs = useCommand<{ id: string; crs: string }>({
    path: (a) => `${markupPath}/${a.id}/crs`,
    body: (a) => ({ crs: a.crs }),
    invalidate: () => [...invalidateMarkup(), qk.all('audit')],
  })

  /* Разметка разложена по страницам: у каждого листа свой слой, и пин соседней
     страницы не должен лечь поверх чужого чертежа. */
  const crsList = crsQuery.data?.rows ?? []
  const byPage = useMemo(() => {
    const m = new Map<number, StoredMarkup[]>()
    for (const row of markupQuery.data ?? []) m.set(row.page, [...(m.get(row.page) ?? []), row])
    return m
  }, [markupQuery.data])
  /* Пара, которую сравнивают сейчас. Нужна, чтобы взять слои разметки обеих
     сторон: разметка хранится по паре (ревизия, версия), и в сравнении их две. */
  const [pair, setPair] = useState<{ before: CompareItem; after: CompareItem } | null>(null)
  /* Полки сравнения сворачиваются, как и полка обычного вида. На 1366 точках
     две полки съедают больше шестисот, и на два чертежа остаётся по триста —
     читать нечем, а убрать было нечем тем более. */
  /* Какое СТАРОЕ замечание выделено. Своё состояние, а не общее с обычным
     видом: слева и справа выделяют разное, и одно на двоих кольцо прыгало бы
     между сторонами. */
  const [oldFocus, setOldFocus] = useState<string | null>(null)
  const [oldOpen, setOldOpen] = useState(true)
  const [newOpen, setNewOpen] = useState(true)
  const raiseBox = useResizable({ sizeKey: 'viewer-compare-new', width: 320, minWidth: 260, anchor: 'right', edges: ['left'] })
  /* Полка слева тянется за ПРАВУЮ кромку: левая у неё прижата к краю окна и
     неподвижна, и ручка на ней двигала бы противоположный край — жест
     наизнанку. anchor не назван: значение по умолчанию и есть «прижата слева».
     Хук поднят из CompareComments: ширину полки должен знать и контейнер
     перехода ширины (ShelfSlide). */
  const oldBox = useResizable({ sizeKey: 'viewer-compare-old', width: 320, minWidth: 260, edges: ['right'] })
  /* Тяга полки «Новое замечание»: пока она идёт, за курсором обязана идти и
     полоса инструментов — её отступ считается от ширины полки. */
  const [raiseDrag, setRaiseDrag] = useState(false)
  /* Личность обработчика неизменна: он стоит в зависимостях эффекта внутри
     сравнения, и новый обработчик на каждом рендере гонял бы эффект по кругу.
     Записывается только настоящая смена пары — иначе то же самое, вид сбоку. */
  const onPair = useCallback((before: CompareItem, after: CompareItem) => {
    setPair((p) => (p && p.before.url === before.url && p.after.url === after.url
      ? p : { before, after }))
  }, [])
  const layerPath = (it: CompareItem | null) =>
    it ? `${base}/revisions/${encodeURIComponent(it.rev)}/versions/${it.ver}/markup` : null
  const beforeLayer = useSub<StoredMarkup[]>(layerPath(pair?.before ?? null))
  /* Обратный перевод: в полке лежит код замечания, а выделение держится ключом
     разметки — иначе строку и метку не свести. */
  const oldFocusCode = (beforeLayer.data ?? []).find((m) => m.id === oldFocus)?.crs ?? null
  const afterLayer = useSub<StoredMarkup[]>(layerPath(pair?.after ?? null))
  /**
   * Разложить ЛЮБОЙ слой разметки по страницам.
   *
   * <p>Вынесено из `byPage`, потому что в режиме сравнения слоёв два: свой у
   * стороны «Было» и свой у стороны «Стало». Правило разбора при этом одно —
   * иначе метка в сравнении встала бы не туда, где стоит в обычном виде, и
   * разошлись бы они молча.
   */
  const pagesOf = (rows: StoredMarkup[] | undefined): Map<number, StoredMarkup[]> => {
    const m = new Map<number, StoredMarkup[]>()
    for (const row of rows ?? []) m.set(row.page, [...(m.get(row.page) ?? []), row])
    return m
  }
  /** Метки чужого слоя — тем же правилом, что и свои. */
  const marksIn = (layer: Map<number, StoredMarkup[]>, n: number): Mark[] =>
    (layer.get(n) ?? []).filter((m) => m.crs).map((m, i) => {
      const at = anchorOf(toShape(m))
      return { id: m.id, n: i + 1, kind: m.kind, x: at.x, y: at.y, crs: crsList.find((c) => c.id === m.crs) ?? null, code: m.crs }
    })
  /** Фигуры чужого слоя: чужой слой всегда только для чтения, снимать в нём нечего. */
  const piecesIn = (layer: Map<number, StoredMarkup[]>, n: number): Piece[] =>
    (layer.get(n) ?? []).filter((m) => m.kind !== 'pin')
      .map((m) => ({ id: m.id, s: toShape(m), crs: m.crs, mine: false,
                     stale: m.crs != null && (m.crsVerdict === 'rejected' || m.crsStatus === 'closed') }))

  /* Фигура на листе — это не только геометрия: от того, привязана ли она к
     замечанию и своя ли она, зависит и вид, и право снять. */
  const piecesOf = (n: number): Piece[] => (byPage.get(n) ?? [])
    .filter((m) => m.kind !== 'pin')
    .map((m) => ({ id: m.id, s: toShape(m), crs: m.crs, mine: m.author === me?.subject,
                   stale: m.crs != null && (m.crsVerdict === 'rejected' || m.crsStatus === 'closed') }))
  /* Метка — любая фигура с замечанием, а не только булавка. Нумерация идёт по
     странице: на листе она и читается. */
  const marksOf = (n: number): Mark[] => (byPage.get(n) ?? []).filter((m) => m.crs).map((m, i) => {
    const at = anchorOf(toShape(m))
    return { id: m.id, n: i + 1, kind: m.kind, x: at.x, y: at.y, crs: crsList.find((c) => c.id === m.crs) ?? null, code: m.crs }
  })
  const stored = byPage.get(page) ?? []
  /* Что показывать поверх чертежа. Разметка полезна, пока её ждут; когда
     смотрят сам чертёж, она мешает — и до сих пор снять её было нечем. */
  const [showShapes, setShowShapes] = useState(true)
  const [showMarks, setShowMarks] = useState(true)
  /* Выбранная фигура. Снять можно только свою и только без замечания — то же
     правило, каким отбивает сервер (403 markup.not-yours, 409 bound-to-crs). */
  const [picked, setPicked] = useState<string | null>(null)
  const pickedPiece = stored.find((m) => m.id === picked) ?? null
  const mayErase = pickedPiece != null && !pickedPiece.crs && pickedPiece.author === me?.subject
  const marks = marksOf(page)
  /* Список замечаний — по всему документу, а не по текущему листу. Прежде он
     строился от `page`, поэтому стоило пролистать ниже — и замечания верхних
     листов пропадали: человек видел пустую панель ровно там, где искал, что
     ему написали. Порядок листов, внутри листа — номера, как на самом чертеже. */
  const sheetsWithMarks = useMemo(
    () => [...byPage.keys()].sort((a, b) => a - b).map((n) => ({ page: n, marks: marksOf(n) })).filter((s) => s.marks.length),
    // marksOf читает byPage и crsList; пересобирать надо при изменении любого.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [byPage, crsList],
  )
  const totalMarks = sheetsWithMarks.reduce((n, s) => n + s.marks.length, 0)
  /* К какой метке перешли. Держится до следующего перехода: кольцо на листе
     должно пережить прокрутку, иначе оно гаснет раньше, чем на него посмотрят. */
  const [focusMark, setFocusMark] = useState<string | null>(null)
  const crsBox = useResizable({ sizeKey: 'viewer-crs', width: 340, minWidth: 260, anchor: 'right', edges: ['left'] })
  /* Полка складывается в полосу: на 1366 она забирает у листа четверть ширины,
     а чертёж читают по месту, а не по описанию. Открыта по умолчанию — за этим
     на документ и приходят; свёрнутое состояние живёт до закрытия просмотрщика
     и на сервер не носится. */
  const [crsOpen, setCrsOpen] = useState(true)
  const goToMark = (sheet: number, m: Mark) => {
    setFocusMark(m.id)
    setPageDraft(String(sheet))
    setGoto((g) => ({ page: sheet, nonce: g.nonce + 1, y: m.y / VB_H }))
  }

  /* Переход с экрана замечаний: открыть ту версию, по которой замечание
     высказано, и подсветить обведённое место. Ревизия с версией приходят
     отдельным запросом — в реестре замечаний версии нет, а показывать
     последнюю значило бы показывать другой чертёж. Наводка одноразовая: дальше
     человек листает ревизии сам, и возвращать его к замечанию нельзя. */
  const spot = useSub<{ doc: string; rev: string | null; ver: number | null; page: number | null; markup: string | null }>(
    crs ? `/crs/${encodeURIComponent(crs)}/location` : null)
  const aimed = useRef<string | null>(null)
  useEffect(() => {
    const at = spot.data
    if (!crs || !at || aimed.current === crs) return
    aimed.current = crs
    if (at.rev) setPickedRev(at.rev)
    if (at.ver != null) setPickedVer(at.ver)
    if (at.page != null) { setPageDraft(String(at.page)); setGoto((g) => ({ page: at.page!, nonce: g.nonce + 1 })) }
    setFocusMark(at.markup)
  }, [crs, spot.data])

  /* Открытие на листе из поиска по содержимому файлов. Наводка одноразовая,
     как и у замечания: дальше человек листает сам, и возвращать его к находке
     нельзя. Ждём числа страниц — до загрузки документа прыгать некуда. */
  const aimedPage = useRef<number | null>(null)
  useEffect(() => {
    if (!openAt || pageCount < 1 || aimedPage.current === openAt) return
    aimedPage.current = openAt
    const target = Math.min(Math.max(1, openAt), pageCount)
    setPageDraft(String(target))
    setGoto((g) => ({ page: target, nonce: g.nonce + 1 }))
  }, [openAt, pageCount])

  /* Версия находки. Показывать последнюю значило бы показывать другой чертёж:
     ровно та ошибка, что была у замечаний до 18.08. Наводка одноразовая —
     дальше человек переключает версии сам. */
  const aimedFile = useRef<string | null>(null)
  useEffect(() => {
    if (!openRev) return
    const key = openRev + ':' + (openVer ?? '')
    if (aimedFile.current === key) return
    aimedFile.current = key
    setPickedRev(openRev)
    if (openVer != null) setPickedVer(openVer)
  }, [openRev, openVer])

  /* Delete снимает выбранную фигуру. Проверка на поле ввода обязательна:
     иначе клавиша стирала бы разметку, пока человек правит номер страницы. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return
      const el = document.activeElement
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return
      if (!mayErase || !picked) return
      e.preventDefault()
      eraseShape.mutate({ id: picked }, { onSuccess: () => setPicked(null) })
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mayErase, picked, eraseShape])

  // Пока номер набирают руками, прокрутка его не перебивает.
  useEffect(() => {
    if (document.activeElement !== pageInputRef.current) setPageDraft(String(page))
  }, [page])
  const jump = (n: number) => {
    if (!Number.isFinite(n)) { setPageDraft(String(page)); return }
    const p = Math.min(Math.max(1, Math.round(n)), pageCount)
    setPageDraft(String(p))
    setGoto((g) => ({ page: p, nonce: g.nonce + 1 }))
    /* В сравнении номер листа ставится ЗДЕСЬ: прокрутки нет, а `page` меняет
       обычно PdfPages, которого в этом виде не смонтировано. Раньше это была
       досада — цифра менялась, листы стояли; теперь на `page` завязаны оба
       слоя разметки и лист выпускаемого замечания, то есть рабочее место
       оставалось запертым на одном листе. */
    if (variant === 'compare') setPage(p)
  }

  // Проверка после хуков: ранний выход до них нарушил бы их порядок.
  if (!doc) {
    const loading = docQuery.isPending || vdocQuery.isPending
    return (
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: 'var(--surface-page)' }}>
        <EmptyState icon="file-x"
                    title={loading ? t(lang, 'Загрузка…', 'Loading…') : t(lang, 'Документ не найден', 'Document not found')}
                    text={no}
                    action={<Button variant="dark" size="sm" onClick={onBack}>{t(lang, 'К реестру', 'Back')}</Button>} />
      </div>
    )
  }

  // Размечать можно только чертёж: поверх таблицы булавке не на что лечь.
  const canMarkUp = markupPath != null && file.url != null && isPdf
  const addPin = (p: Pt, n: number) => { setDraftPin({ ...p, page: n }); setDraftMark(null); setTool('cursor') }
  const markedPage = draftPin?.page ?? draftMark?.page ?? null
  const submitCrs = () => {
    if (!canRaise) {
      toast({ tone: 'info', title: iApproveHere ? 'Утверждающий замечаний не выпускает' : 'Рассмотрение не идёт',
              text: iApproveHere
                ? 'Ваш шаг — решение по замечаниям рецензентов и правка формулировок.'
                : 'Замечание выпускается внутри рассмотрения — там, где вам поручен шаг.' })
      return
    }
    if (markedPage == null) {
      toast({ tone: 'info', title: 'Не указано место', text: 'Поставьте булавку или обведите место на листе.' })
      return
    }
    if (!draftText.trim()) return
    const at = draftPin
    const mark = draftMark
    raiseCrs.mutate({ text: draftText.trim(), crit: draftCrit, page: markedPage }, {
      onSuccess: (result) => {
        // Место привязывается к выпущенному коду, а не к порядковому номеру.
        if (at) pinCrs.mutate({ at, crs: result.raised })
        else if (mark) attachCrs.mutate({ id: mark.id, crs: result.raised })
        toast({ tone: 'success', title: `Замечание ${result.raised} выпущено`, text: `${no}, рев. ${rev ?? '—'}` })
        setDraftPin(null)
        setDraftMark(null)
        setDraftText('')
        /* Инструмент возвращается сам. Постановка булавки переключает на курсор,
           чтобы случайный клик не наплодил булавок, но после выпуска замечания
           это оборачивалось мёртвым листом: следующее замечание удавалось
           поставить только после переоткрытия документа. Замечания идут сериями. */
        setTool('pin')
      },
    })
  }

  /* Полоса переносится по строкам. Десять групп просят около 1800 px, на
     ноутбуке 1366 их не бывает, а прокрутки у полосы нет и быть не может:
     корень приложения — position: fixed, переполнение просто уезжает за край
     экрана. В одну строку не влезает ничто из того, что можно спрятать: даже
     обе выгрузки вместе — это 60 px из пятисот недостающих. Поэтому вторая
     строка, а не меню «ещё»: она стоит 40 px высоты и не прячет ничего.
     Порядок задаёт приоритет — первой строке достаются ревизии и листы,
     второй вид, зум и выгрузки. */
  const Toolbar = (
    <div style={{ minHeight: 52, flex: '0 0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '6px 14px', background: 'var(--surface-header)', borderBottom: '1px solid var(--border-header)' }}>
      <Button variant="dark" size="sm" iconLeft={<Icon name="arrow-left" size={14} />} onClick={onBack}>{t(lang, 'К реестру', 'Back')}</Button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 4 }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600, color: 'var(--slate-100)' }}>{doc.no}</span>
        <span style={{ font: 'var(--font-ui)', fontSize: 12.5, color: 'var(--slate-400)', maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.title}</span>
      </div>
      {/* Ревизии настоящие: раньше здесь стояли «A», «B» и текущая — набор,
          придуманный на месте и не совпадавший с историей документа. */}
      <div style={{ display: 'flex', border: '1px solid var(--border-header)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
        {/* Hover только у невыбранных: выбранная ревизия рисуется amber из
            состояния инлайном, и правило без модификатора перекрашивало бы её.
            !important — инлайновый фон бьёт классовое правило (приём
            .tdms-rail:hover). transform в transition дописан руками: инлайновый
            transition перекрывает классовый у tool-btn целиком. */}
        <style>{'@media (hover: hover) and (pointer: fine) { .tdms-revchip[aria-pressed=\'false\']:hover { background: rgba(255,255,255,.06) !important; } }'}</style>
        {revList.length === 0 ? (
          <span style={{ padding: '5px 9px', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--slate-400)' }}>{t(lang, 'ревизий нет', 'no revisions')}</span>
        ) : revList.map((r) => (
          <button key={r} onClick={() => { setPickedRev(r); setPickedVer(null); setDraftPin(null); setDraftMark(null); setPage(1); setPageCount(1) }}
                  className="tool-btn tdms-revchip" aria-pressed={r === rev}
                  style={{ padding: '5px 9px', border: 0, fontFamily: 'var(--font-mono)', fontSize: 11, cursor: 'pointer', color: r === rev ? 'var(--slate-900)' : 'var(--slate-300)', background: r === rev ? 'var(--amber-400)' : 'transparent', transition: 'background var(--duration-fast) var(--ease-standard), color var(--duration-fast) var(--ease-standard), transform var(--duration-press) var(--ease-out)' }}>рев. {r}</button>
        ))}
      </div>
      {/* В списке ВСЕ файлы ревизии, но вспомогательные названы своими именами:
          «версией чертежа» исходник не является, хотя счётчик у них общий.

          Прежде список показывал только основные — и приложенный Excel нельзя
          было открыть вовсе: предпросмотр для него есть, а дороги к нему нет.
          Прятать вспомогательные от просмотрщика значило запереть ровно те
          файлы, ради которых предпросмотр и делался. */}
      {versions.length > 1 ? (
        <Select size="sm" value={String(ver ?? '')} onChange={(e) => { setPickedVer(+e.target.value); setDraftPin(null); setDraftMark(null); setPage(1); setPageCount(1) }} block={false} style={{ maxWidth: 220 }}>
          {versions.map((v) => (
            <option key={v.v} value={v.v}>
              {(v.kind ?? 'main') === 'main'
                ? `${t(lang, 'версия', 'version')} ${v.v}`
                : `${(v.kind === 'original' ? t(lang, 'исходник', 'source') : t(lang, 'вложение', 'attachment'))} · ${v.file}`}
            </option>
          ))}
        </Select>
      ) : null}
      {/* Переход по страницам. У одностраничного файла показывать нечего. */}
      {pageCount > 1 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {/* Radix-подсказки вместо нативных title: без секундной задержки ОС
              и с мгновенным показом соседних (skipDelay провайдера). */}
          <Tooltip label={t(lang, 'Предыдущая страница', 'Previous page')}>
            <button onClick={() => jump(page - 1)} disabled={page <= 1}
                    className="icon-btn" style={{ ...hdrBtn, opacity: page <= 1 ? 0.4 : 1, cursor: page <= 1 ? 'not-allowed' : 'pointer' }}><Icon name="chevron-left" size={15} /></button>
          </Tooltip>
          <input ref={pageInputRef} value={pageDraft} inputMode="numeric"
                 title={t(lang, 'Номер страницы', 'Page number')}
                 onChange={(e) => setPageDraft(e.target.value.replace(/\D/g, ''))}
                 onKeyDown={(e) => { if (e.key === 'Enter') { jump(Number(pageDraft)); e.currentTarget.blur() } }}
                 onBlur={() => jump(Number(pageDraft))}
                 style={{ width: 40, height: 30, textAlign: 'center', border: '1px solid var(--border-header)', borderRadius: 'var(--radius-sm)', background: 'transparent', color: 'var(--slate-100)', fontFamily: 'var(--font-mono)', fontSize: 11 }} />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--slate-400)' }}>/ {pageCount}</span>
          <Tooltip label={t(lang, 'Следующая страница', 'Next page')}>
            <button onClick={() => jump(page + 1)} disabled={page >= pageCount}
                    className="icon-btn" style={{ ...hdrBtn, opacity: page >= pageCount ? 0.4 : 1, cursor: page >= pageCount ? 'not-allowed' : 'pointer' }}><Icon name="chevron-right" size={15} /></button>
          </Tooltip>
        </div>
      ) : null}
      {/* Состояние файла — в тулбаре, а не только поверх листа: при прокрутке
          плашка уходит из виду, а вопрос «оно грузится или сломалось?»
          остаётся. Показывается, только пока есть что сказать. */}
      {isPdf && file.url && !pdfState.done ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginLeft: 10, padding: '3px 9px', borderRadius: 999, background: 'rgba(255,255,255,.08)' }}>
          <SpinIcon size={13} style={{ color: 'var(--amber-300)' }} />
          <span style={{ font: 'var(--font-ui)', fontSize: 11.5, color: 'var(--slate-200)', fontFeatureSettings: '"tnum" 1' }}>
            {pdfState.percent == null
              ? t(lang, 'Открываем документ…', 'Opening…')
              : t(lang, `Загрузка ${pdfState.percent}%`, `Downloading ${pdfState.percent}%`)}
            {/* Размер знает и тулбар: плашка на листе уходит при прокрутке. */}
            {pdfState.total ? ' · ' + fileSize(pdfState.total, lang) : null}
          </span>
        </span>
      ) : null}
      {isPdf && file.url && pdfState.failed ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginLeft: 10, padding: '3px 9px', borderRadius: 999, background: 'rgba(220,38,38,.18)' }}>
          <Icon name="alert-triangle" size={13} />
          <span style={{ font: 'var(--font-ui)', fontSize: 11.5, color: 'var(--slate-100)' }}>
            {t(lang, 'Файл не открылся', 'File failed to open')}
          </span>
        </span>
      ) : null}
      <div style={{ flex: 1 }} />
      <Segmented size="sm" value={variant} onChange={setVariant} options={[
        { value: 'classic', label: t(lang, 'Классический', 'Classic') },
        { value: 'focus', label: t(lang, 'Фокус', 'Focus') },
        { value: 'compare', label: t(lang, 'Сравнение', 'Compare') },
      ]} />
      {/* Поиск по тексту документа. Находки называют лист: просмотрщик умеет
          открыться на нём, и «нашлось на листе 47» дороже, чем «нашлось». */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ width: 190 }}>
          <Input size="sm" value={needle} onChange={(e) => setNeedle(e.target.value)}
                 placeholder={t(lang, 'Поиск по документу…', 'Search in the document…')}
                 leading={<Icon name="search" size={13} />}
                 onKeyDown={(e) => { if (e.key === 'Enter') void runSearch() }} />
        </div>
        {hits ? (
          <span style={{ font: 'var(--font-caption)', color: 'var(--slate-300)' }}>
            {searching ? t(lang, 'ищем…', 'searching…')
              : hits.length
                ? t(lang, `листов: ${hits.length}`, `${hits.length} sheet(s)`)
                : t(lang, 'не найдено', 'nothing found')}
          </span>
        ) : null}
      </div>
      {/* Про Ctrl+колесо надо сказать: жест в интерфейсе не виден, а искать его
          наугад никто не станет. Подсказка живёт на счётчике процентов, а не на
          всей группе: нативный title группы всплывал бы поверх Radix-подсказок
          её кнопок. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 4 }}>
        <Tooltip label={t(lang, 'Уменьшить', 'Zoom out')}>
          <button onClick={() => setScale((s) => Math.max(0.5, s - 0.15))} className="icon-btn" style={hdrBtn}
                  aria-label={t(lang, 'Уменьшить', 'Zoom out')}><Icon name="zoom-out" size={15} /></button>
        </Tooltip>
        <span title={t(lang, 'Колесо — листать, Ctrl + колесо — масштаб', 'Wheel scrolls, Ctrl + wheel zooms')}
              style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--slate-300)', width: 38, textAlign: 'center' }}>{Math.round(scale * 100)}%</span>
        <Tooltip label={t(lang, 'Увеличить', 'Zoom in')}>
          <button onClick={() => setScale((s) => Math.min(2.5, s + 0.15))} className="icon-btn" style={hdrBtn}
                  aria-label={t(lang, 'Увеличить', 'Zoom in')}><Icon name="zoom-in" size={15} /></button>
        </Tooltip>
      </div>
      {/* Показывать ли слои поверх чертежа. Разметка нужна, пока её читают;
          когда смотрят сам чертёж, она мешает — и снять её было нечем.
          Замечания и пометки разведены: у них разный вес, и прячут их по
          разным причинам. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8, paddingLeft: 8, borderLeft: '1px solid var(--slate-700)' }}>
        <Tooltip label={showMarks ? t(lang, 'Скрыть замечания', 'Hide comments') : t(lang, 'Показать замечания', 'Show comments')}>
          <button onClick={() => setShowMarks((v) => !v)} className="icon-btn"
                  style={{ ...hdrBtn, color: showMarks ? 'var(--amber-400)' : 'var(--slate-400)' }}>
            <Icon name="map-pin" size={15} />
          </button>
        </Tooltip>
        <Tooltip label={showShapes ? t(lang, 'Скрыть пометки', 'Hide markup') : t(lang, 'Показать пометки', 'Show markup')}>
          <button onClick={() => setShowShapes((v) => !v)} className="icon-btn"
                  style={{ ...hdrBtn, color: showShapes ? 'var(--amber-400)' : 'var(--slate-400)' }}>
            <Icon name={showShapes ? 'eye' : 'eye-off'} size={15} />
          </button>
        </Tooltip>
        {/* Обводки прошлой ревизии. Их переносят руками — обводили те же места
            на прошлом круге, и повторять их заново значит терять час на лист. */}
        {/* Обёртка-span обязательна: выключенная кнопка не шлёт событий
            указателя, и Radix без неё подсказку не покажет. В Firefox события
            над disabled-элементом не всплывают даже до обёртки, поэтому
            выключенной кнопке ставится pointerEvents: none — наведение
            проходит сквозь неё на span (стандартный приём Radix). */}
        <Tooltip label={t(lang, 'Перенести обводки незакрытых замечаний с прошлой ревизии',
                              'Carry over markup of open comments from the previous revision')}>
          <span style={{ display: 'inline-flex' }}>
            <button disabled={!canMarkUp || carryOver.isPending}
                    onClick={() => carryOver.mutate(undefined, {
                      onSuccess: (r) => toast({ tone: 'success', title: t(lang, 'Обводки перенесены', 'Markup carried over'),
                                                text: t(lang, `С ревизии ${r.from}: ${r.carried}`, `From revision ${r.from}: ${r.carried}`) }),
                      onError: (e: unknown) => toast({ tone: 'info', title: t(lang, 'Переносить нечего', 'Nothing to carry over'),
                                                       text: e instanceof Error ? e.message : String(e) }),
                    })} className="icon-btn"
                    style={{ ...hdrBtn, color: canMarkUp ? 'var(--slate-300)' : 'var(--slate-600)',
                             pointerEvents: !canMarkUp || carryOver.isPending ? 'none' : undefined }}>
              <Icon name="history" size={15} />
            </button>
          </span>
        </Tooltip>
        {/* Лист с разметкой картинкой: обведённое место прикладывают к
            протоколу и к письму, и до сих пор его пересобирали снимком экрана. */}
        {/* Весь документ с разметкой — файлом с сервера: его подшивают в дело
            и прикладывают к письму, а собрать его можно было только в
            стороннем редакторе. */}
        <Tooltip label={t(lang, 'Скачать документ с разметкой (PDF)', 'Download the document with markup (PDF)')}>
          <button onClick={() => {
                    if (!rev || ver == null) return
                    saveFile(`/docs/${encodeURIComponent(no)}/revisions/${encodeURIComponent(rev)}/versions/${ver}/markup.pdf`)
                      .then((name) => toast({ tone: 'success', title: t(lang, 'Файл собран', 'File ready'), text: name }))
                      .catch((e: unknown) => toast({ tone: 'danger', title: t(lang, 'Не собрался', 'Failed'),
                                                     text: e instanceof Error ? e.message : String(e) }))
                  }} className="icon-btn"
                  style={{ ...hdrBtn, color: 'var(--slate-300)' }}>
            <Icon name="file-text" size={15} />
          </button>
        </Tooltip>
        <Tooltip label={t(lang, 'Скачать лист с разметкой (PNG)', 'Download the sheet with markup (PNG)')}>
          <button onClick={() => {
                    exportSheetPng(page, `${no}_${rev ?? ''}_${t(lang, 'лист', 'sheet')}${page}.png`)
                      .then(() => toast({ tone: 'success', title: t(lang, 'Лист сохранён', 'Sheet saved'),
                                          text: t(lang, `Страница ${page} с разметкой`, `Page ${page} with markup`) }))
                      .catch((e: unknown) => toast({ tone: 'danger', title: t(lang, 'Не сохранилось', 'Not saved'),
                                                     text: e instanceof Error ? e.message : String(e) }))
                  }} className="icon-btn"
                  style={{ ...hdrBtn, color: 'var(--slate-300)' }}>
            <Icon name="download" size={15} />
          </button>
        </Tooltip>
      </div>
    </div>
  )

  const ToolRail = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: 8, background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', boxShadow: variant === 'focus' ? 'var(--shadow-md)' : 'none', opacity: canMarkUp ? 1 : 0.5 }}
         title={canMarkUp ? undefined : t(lang, 'Разметка ставится поверх загруженного файла', 'Markup needs an uploaded file')}>
      {TOOLS.map((tl) => (
        <button key={tl.id} disabled={!canMarkUp} onClick={() => setTool(tl.id)} title={tl.ru} className="tool-btn"
                style={{ display: 'grid', placeItems: 'center', width: 36, height: 36, border: 0, borderRadius: 'var(--radius-sm)', cursor: canMarkUp ? 'pointer' : 'not-allowed', background: tool === tl.id ? 'var(--accent-soft)' : 'transparent', color: tool === tl.id ? 'var(--accent-ink)' : 'var(--text-muted)', boxShadow: tool === tl.id ? 'inset 0 0 0 1px var(--accent-line)' : 'none', transition: 'background var(--duration-fast) var(--ease-standard), color var(--duration-fast) var(--ease-standard), transform var(--duration-press) var(--ease-out)' }}><Icon name={tl.icon} size={17} /></button>
      ))}
      {tool === 'stamp' ? <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 6, marginTop: 2, display: 'flex', flexDirection: 'column', gap: 4 }}>{STAMPS.map((s) => <button key={s} onClick={() => setStamp(s)} className="tool-btn" style={{ padding: '4px 6px', border: '1px solid ' + (stamp === s ? 'var(--accent)' : 'var(--border-default)'), borderRadius: 2, background: stamp === s ? 'var(--accent-soft)' : 'transparent', fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', cursor: 'pointer' }}>{s}</button>)}</div> : null}
      {/* Кому видна следующая фигура. Замечание сюда не относится: с ним
          разметка уходит второй стороне вместе с выпуском листа. */}
      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 6, marginTop: 2 }}>
        {/* tool-btn и transition — как у TOOLS выше: единственная кнопка столбца
            без отклика заметнее, чем одинаковое отсутствие у всех. */}
        <button disabled={!canMarkUp} onClick={() => setShare((s) => !s)} className="tool-btn"
                title={share
                  ? t(lang, 'Новые фигуры видны и подрядчику', 'New shapes are visible to the contractor')
                  : t(lang, 'Новые фигуры видны только стороне заказчика', 'New shapes stay on the customer side')}
                style={{ display: 'grid', placeItems: 'center', width: 36, height: 36, border: 0, borderRadius: 'var(--radius-sm)', cursor: canMarkUp ? 'pointer' : 'not-allowed', background: share ? 'var(--accent-soft)' : 'transparent', color: share ? 'var(--accent-ink)' : 'var(--text-muted)', boxShadow: share ? 'inset 0 0 0 1px var(--accent-line)' : 'none', transition: 'background var(--duration-fast) var(--ease-standard), color var(--duration-fast) var(--ease-standard), transform var(--duration-press) var(--ease-out)' }}><Icon name={share ? 'eye' : 'eye-off'} size={17} /></button>
      </div>
      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 6, marginTop: 2 }}>
        {/* Снимается ВЫБРАННАЯ фигура: раньше кнопка снимала последнюю свою, и
            убрать что-то из середины было нечем. Чужую разметку и разметку с
            замечанием сервер не отдаёт снимать, поэтому кнопка объясняет, что
            именно мешает, а не гаснет молча. */}
        <button disabled={!mayErase || eraseShape.isPending}
                onClick={() => { if (mayErase && picked) eraseShape.mutate({ id: picked }, { onSuccess: () => setPicked(null) }) }}
                title={eraseHint(pickedPiece, me?.subject, lang)}
                className="tool-btn"
                style={{ display: 'grid', placeItems: 'center', width: 36, height: 36, border: 0, borderRadius: 'var(--radius-sm)', cursor: mayErase ? 'pointer' : 'not-allowed', background: 'transparent', color: 'var(--danger-ink)', opacity: mayErase ? 1 : 0.4 }}><Icon name="eraser" size={17} /></button>
      </div>
    </div>
  )

  /* Слой разметки заводится на каждый лист свой. Над не-чертежом его нет вовсе:
     пустой холст поверх панели со скачиванием только перехватывал бы нажатия. */
  const overlayFor = (n: number) => (
    <Canvas tool={tool} stamp={stamp} pieces={piecesOf(n)}
            onDraw={(s) => drawShape.mutate({ shape: s, page: n }, {
              // Нарисованное сразу становится отмеченным местом: обводят,
              // чтобы сказать про обведённое, и лишний шаг здесь не нужен.
              onSuccess: (r) => { setDraftMark({ id: r.id, page: n, kind: s.t }); setDraftPin(null) },
            })}
            marks={marksOf(n)} draftPin={draftPin?.page === n ? draftPin : null}
            addPin={(p) => addPin(p, n)} readOnly={!canMarkUp} focusId={focusMark}
            showShapes={showShapes} showPins={showMarks}
            picked={picked} onPick={setPicked} />
  )

  /* Сторона «Стало» — та самая версия, что открыта в просмотрщике? Только на
     ней выпускают замечания: замечание на архивную версию подрядчик не увидит
     вовсе, а команды разметки пишут в слой ОТКРЫТОЙ пары. Совпали — работаем
     тем же слоем и теми же командами, что в обычном виде; не совпали — сторона
     только для чтения, и причина названа рядом. */
  const afterIsOpen = pair != null && pair.after.rev === rev && pair.after.ver === ver
  /* Рисовать и ВЫПУСКАТЬ — разные права, и путать их нельзя. Обводку, стрелку
     и штамп кладут и вне круга: для себя или чтобы показать второй стороне, —
     это разрешает и обычный вид, и сервер (команда разметки про круг не
     спрашивает). Замечание же выпускается только внутри рассмотрения.

     Пока они были одним условием, инструменты в сравнении не появлялись вовсе
     у того, у кого не открыт шаг, — и полка выглядела пустой без объяснения. */
  const mayDrawHere = afterIsOpen && canMarkUp

  /**
   * Слой поверх стороны сравнения.
   *
   * <p>Слева — метки ПРОШЛОЙ подачи: что было сказано и ГДЕ именно. Справа
   * новый лист чистый, и это и есть ответ на вопрос круга «учтено ли
   * замечание» — раньше его собирали, переключаясь между версиями.
   *
   * <p>Рисуется тем же Canvas, что и обычный вид: вторая реализация меток
   * разошлась бы с первой на первой же правке, и разошлась бы молча.
   * Координаты хранятся долями листа, а viewBox растягивается на холст —
   * пересчитывать под масштаб сравнения нечего.
   */
  const compareOverlay = (side: 'before' | 'after') => {
    if (side === 'after' && mayDrawHere) return overlayFor(page)
    const layer = pagesOf((side === 'before' ? beforeLayer : afterLayer).data)
    /* Выделение работает и на СТОРОНЕ, доступной только для чтения: замечание
       выбирают, чтобы найти его место на чертеже, а не чтобы что-то менять.
       Подсвечивается и метка, и сама фигура — обводку от булавки отличают
       глазом, и «где именно» отвечает как раз фигура. */
    const mark = side === 'before' ? oldFocus : null
    return (
      <Canvas tool="cursor" stamp={stamp} pieces={piecesIn(layer, page)}
              onDraw={() => {}} marks={marksIn(layer, page)} draftPin={null}
              addPin={() => {}} readOnly focusId={side === 'before' ? oldFocus : focusMark}
              showShapes={showShapes} showPins={showMarks}
              picked={mark} onPick={(id) => { if (side === 'before') setOldFocus(id) }} />
    )
  }

  /* Пометка о недействующей ревизии — полосой над листом. На самом листе она
     уже стоит (её наносит сервер), но человек смотрит на чертёж, а не на
     штамп, и на не-PDF нанести её нечем вовсе. */
  const Sheet = !file.url || !isPdf ? (
    <div style={{ flex: 1, minWidth: 0, position: 'relative', background: 'var(--slate-800)' }}>
      {file.url && !isPdf
        ? kind === 'sheet' ? <SheetPreview url={file.url} name={fileName ?? ''} lang={lang} />
        : kind === 'doc' ? <DocPreview url={file.url} name={fileName ?? ''} lang={lang} />
        : <OtherFileSheet url={file.url} name={fileName ?? ''} lang={lang} />
        /* Пока файл едет — так и говорим. Прежде здесь стояло «Файл не
           загружен» с советом приложить его в карточке: на копии со штампом
           это держалось секундами и читалось как потерянный файл. */
        : file.loading ? <LoadingSheet lang={lang} />
        : <NoSheet lang={lang} rev={rev} note={file.error} />}
    </div>
  ) : (
    <PdfPages url={file.url} scale={scale} onScale={setScale} onPages={setPageCount} onCurrent={setPage}
              onLoading={onPdfLoading} goto={goto} overlay={overlayFor} lang={lang} />
  )
  const SheetArea = file.notice ? (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: 'var(--danger-bg)', borderBottom: '1px solid var(--danger-line)', color: 'var(--danger-ink)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', fontWeight: 600 }}>
        <Icon name="alert-triangle" size={15} />
        <span>{file.notice}</span>
        <span style={{ marginLeft: 'auto', fontWeight: 400 }}>{t(lang, 'копия справочная — та же пометка стоит на листе', 'reference copy — the sheet carries the same notice')}</span>
      </div>
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>{Sheet}</div>
    </div>
  ) : Sheet

  /**
   * Форма выпуска замечания.
   *
   * <p>Вынесена, потому что мест у неё два: полка обычного вида и правая полка
   * режима сравнения. Вторая копия разошлась бы с первой на первой же правке
   * правила — и разошлась бы молча, а речь о том, кому вообще позволено
   * выпускать замечание.
   */
  const RaiseFormBody = (
      <div style={{ padding: 12, borderTop: '1px solid var(--border-subtle)' }}>
        {/* Пока рассмотрения нет, форма молчит о месте и говорит о главном:
            выпускать замечание не в цикле теперь нельзя, и это не поломка. */}
        {!canRaise ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, font: 'var(--font-caption)', color: 'var(--text-subtle)', marginBottom: 6 }}>
            <Icon name="lock" size={13} />
            {iApproveHere
              ? t(lang, 'Ваш шаг — утверждение: решение по замечаниям и правка формулировок, а не выпуск',
                        'Your step is approval: deciding on comments and fixing wording, not raising them')
              : t(lang, 'Замечание выпускается внутри рассмотрения — там, где вам поручен шаг',
                        'A comment is raised inside a review, on the step assigned to you')}
          </div>
        ) : null}
        <div style={{ font: 'var(--font-caption)', color: markedPage != null ? 'var(--success-ink)' : 'var(--text-subtle)', marginBottom: 6 }}>
          {draftPin
            ? t(lang, `Булавка на листе ${draftPin.page}`, `Pin on sheet ${draftPin.page}`)
            : draftMark
              ? t(lang, `Отмечено: ${SHAPE_NAME[draftMark.kind]?.ru ?? draftMark.kind} на листе ${draftMark.page}`,
                  `Marked: ${SHAPE_NAME[draftMark.kind]?.en ?? draftMark.kind} on sheet ${draftMark.page}`)
              : t(lang, 'Поставьте булавку или обведите место на листе', 'Place a pin or draw around the spot')}
        </div>
        <Input value={draftText} onChange={(e) => setDraftText(e.target.value)} disabled={!canRaise || markedPage == null}
               placeholder={t(lang, 'Текст замечания…', 'Comment text…')} />
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          {/* Вместо кода — критичность: код документа рецензент не ставит. */}
          <Select size="sm" value={draftCrit} onChange={(e) => setDraftCrit(e.target.value)} block={false} style={{ width: 104 }}
                  title={t(lang, 'Критичность замечания', 'Comment criticality')}>
            <option value="high">{t(lang, 'высокая', 'high')}</option>
            <option value="med">{t(lang, 'средняя', 'medium')}</option>
            <option value="low">{t(lang, 'низкая', 'low')}</option>
          </Select>
          <Button variant="primary" size="sm" block iconLeft={<Icon name="message-square-plus" size={14} />}
                  disabled={!canRaise || markedPage == null || !draftText.trim() || raiseCrs.isPending}
                  title={canRaise
                    ? t(lang, `Замечание уйдёт в рассмотрение ${reviewTrm}`, `The comment goes into review ${reviewTrm}`)
                    : iApproveHere
                      ? t(lang, 'Утверждающий решает по замечаниям, а не выпускает их', 'The approver decides on comments, not raises them')
                      : t(lang, 'Документ сейчас не на вашем рассмотрении', 'This document is not on your review right now')}
                  onClick={submitCrs}>{raiseCrs.isPending ? t(lang, 'Выпуск…', 'Raising…') : t(lang, 'В CRS', 'Add to CRS')}</Button>
        </div>
      </div>
  )

  const CrsRail = (
    /* Ширину полки тянут за левый край. Замечание бывает длиннее полосы в 340 px,
       и читать его, догадываясь по трём словам, нельзя. Размер запоминается —
       ключ свой, чтобы полка не делила его с окнами. */
    <div ref={crsBox.ref}
         style={{ width: crsBox.width, flex: '0 0 auto', position: 'relative', display: 'flex', flexDirection: 'column', background: 'var(--surface-card)', borderLeft: '1px solid var(--border-default)' }}>
      <ResizeHandles box={crsBox} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
        <span style={{ color: 'var(--accent-ink)', display: 'inline-flex' }}><Icon name="message-square-warning" size={16} /></span>
        <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Замечания по документу', 'Document comments')}</span>
        <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>{totalMarks}</span>
        <button onClick={() => setCrsOpen(false)} className="icon-btn"
                aria-label={t(lang, 'Свернуть полку замечаний', 'Collapse the comments shelf')}
                title={t(lang, 'Свернуть полку замечаний', 'Collapse the comments shelf')}
                style={railBtn}><Icon name="panel-right-close" size={15} /></button>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {sheetsWithMarks.map((s) => (
          <Fragment key={s.page}>
            {/* Лист подписан у каждой пачки: номер на кружке — внутрилистовой,
                и без подписи «3» с седьмого листа не отличить от «3» с первого. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 2 }}>
              <span style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: s.page === page ? 'var(--accent-ink)' : 'var(--text-subtle)' }}>
                {t(lang, 'Лист', 'Sheet')} {s.page}
              </span>
              {s.page === page ? <Badge tone="accent" size="sm">{t(lang, 'на экране', 'on screen')}</Badge> : null}
              <span style={{ flex: 1, height: 1, background: 'var(--border-subtle)' }} />
            </div>
            {s.marks.map((p) => (
              <button
                key={p.id}
                onClick={() => goToMark(s.page, p)}
                className="tdms-rail"
                title={t(lang, 'Перейти к замечанию на листе', 'Go to the comment on the sheet')}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                  border: '1px solid ' + (p.id === focusMark ? 'var(--accent)' : 'var(--border-default)'),
                  borderLeft: '3px solid var(--code-3)', borderRadius: 'var(--radius-sm)', padding: '10px 12px',
                  background: p.id === focusMark ? 'var(--accent-soft)' : 'transparent',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ display: 'grid', placeItems: 'center', width: 20, height: 20, borderRadius: 999, background: 'var(--code-3)', color: '#fff', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, flex: '0 0 auto' }}>{p.n}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--text-strong)' }}>{p.code ?? '—'}</span>
                  {p.crs ? <CritBadge level={p.crs.crit} lang={lang} /> : null}
                  <span style={{ marginLeft: 'auto', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{p.crs?.clause ?? ''}</span>
                </div>
                <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', marginTop: 6 }}>
                  {p.crs ? p.crs.text : t(lang, 'Замечание не найдено в реестре', 'Comment not found in the register')}
                </div>
                {/* Судьба замечания и ответ подрядчика. Прежде полка показывала
                    только текст: «на что уже ответили» и «что закрыто»
                    смотрели на другом экране, переключаясь туда-обратно. */}
                {p.crs ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 7 }}>
                    <Badge tone={p.crs.status === 'closed' ? 'success' : p.crs.status === 'answered' ? 'info' : 'warning'} size="sm">
                      {p.crs.status === 'closed' ? t(lang, 'закрыто', 'closed')
                        : p.crs.status === 'answered' ? t(lang, 'есть ответ', 'answered')
                        : t(lang, 'открыто', 'open')}
                    </Badge>
                    {p.crs.verdict === 'rejected' ? <Badge tone="neutral" size="sm">{t(lang, 'снято', 'rejected')}</Badge> : null}
                    {p.crs.sheet ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-subtle)' }}>{p.crs.sheet}</span> : null}
                  </div>
                ) : null}
                {p.crs?.acsr ? (
                  <div style={{ marginTop: 6, padding: '6px 8px', borderRadius: 'var(--radius-sm)', background: 'var(--surface-sunken)' }}>
                    <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginBottom: 2 }}>
                      {t(lang, 'Ответ подрядчика', 'Contractor answer')} · {p.crs.acsr.date ?? ''}
                    </div>
                    <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{p.crs.acsr.text}</div>
                  </div>
                ) : null}
                {/* Кто поставил и когда. На чертёж смотрят несколько человек
                    сразу, и «чьё это замечание» — первый вопрос к строке. */}
                {p.crs ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 7, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
                    <Avatar name={p.crs.author} size={16} />
                    <span style={{ color: 'var(--text-muted)' }}>{p.crs.author}</span>
                    <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)' }}>{p.crs.date}</span>
                  </div>
                ) : null}
              </button>
            ))}
          </Fragment>
        ))}
        {totalMarks === 0 ? (
          <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)', padding: '8px 2px' }}>
            {t(lang, 'На этой версии замечаний по разметке нет.', 'No sheet comments on this version.')}
          </div>
        ) : null}
      </div>
      {RaiseFormBody}
    </div>
  )


  /* Свёрнутая полка — полоса в 38 px: возврат виден на месте, а не угадывается,
     и число замечаний остаётся на глазах. */
  const CrsStrip = (
    <div style={{ width: 38, flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '10px 0', background: 'var(--surface-card)', borderLeft: '1px solid var(--border-default)' }}>
      <button onClick={() => setCrsOpen(true)} className="icon-btn"
              aria-label={t(lang, 'Развернуть полку замечаний', 'Expand the comments shelf')}
              title={t(lang, 'Развернуть полку замечаний', 'Expand the comments shelf')}
              style={railBtn}><Icon name="panel-right-open" size={15} /></button>
      <span style={{ color: 'var(--accent-ink)', display: 'inline-flex' }}><Icon name="message-square-warning" size={15} /></span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>{totalMarks}</span>
    </div>
  )

  /* Поиск по тексту: сервер отвечает листами и выдержками, экран открывает
     первый найденный лист. Пустой запрос снимает находки, а не ищет пустоту. */
  const runSearch = async () => {
    const q = needle.trim()
    if (!q || !rev) { setHits(null); return }
    setSearching(true)
    try {
      const found = await request<{ rows: { page: number; snippet: string; ocr: boolean }[] }>(
        'GET', `/docs/${encodeURIComponent(no)}/revisions/${encodeURIComponent(rev)}/text`
          + `?q=${encodeURIComponent(q)}${ver == null ? '' : `&ver=${ver}`}`)
      setHits(found.rows)
      if (found.rows.length) setGoto({ page: found.rows[0]!.page, nonce: Date.now() })
    } catch (e) {
      toast({ tone: 'danger', title: t(lang, 'Поиск не удался', 'Search failed'),
        text: e instanceof Error ? e.message : String(e) })
    } finally {
      setSearching(false)
    }
  }

  /** Свёрнутая полка сравнения — полоса в 38 px, как в обычном виде. */
  const compareStrip = (side: 'left' | 'right', icon: string, label: string, onOpen: () => void, badge?: ReactNode) => (
    <div style={{ width: 38, flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  padding: '10px 0', background: 'var(--surface-card)',
                  borderLeft: side === 'right' ? '1px solid var(--border-default)' : undefined,
                  borderRight: side === 'left' ? '1px solid var(--border-default)' : undefined }}>
      <button onClick={onOpen} className="icon-btn" aria-label={label} title={label} style={railBtn}>
        <Icon name={side === 'left' ? 'panel-left-open' : 'panel-right-open'} size={15} />
      </button>
      <span style={{ color: 'var(--accent-ink)', display: 'inline-flex' }}><Icon name={icon} size={15} /></span>
      {badge}
    </div>
  )

  /**
   * Правая полка сравнения: рабочее место рецензента.
   *
   * <p>Инструменты графики и та же форма выпуска, что в обычном виде. Замечание
   * ставится на ту версию, КОТОРУЮ РАССМАТРИВАЮТ, — то есть на открытую в
   * просмотрщике. Выбрана в «Стало» другая — сторона только для чтения, и
   * причина названа: замечание на архивную версию подрядчик не увидит вовсе.
   */
  const CompareRaiseRail = (
    <ShelfSlide open={newOpen} width={raiseBox.width} onDragging={setRaiseDrag}
                strip={compareStrip('right', 'message-square-plus',
                  t(lang, 'Развернуть выпуск замечания', 'Expand the new comment shelf'), () => setNewOpen(true))}>
    <div ref={raiseBox.ref}
         style={{ width: raiseBox.width, flex: '0 0 auto', position: 'relative', display: 'flex', flexDirection: 'column',
                  background: 'var(--surface-card)', borderLeft: '1px solid var(--border-default)' }}>
      <ResizeHandles box={raiseBox} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
        <span style={{ color: 'var(--accent-ink)', display: 'inline-flex' }}><Icon name="message-square-plus" size={16} /></span>
        <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Новое замечание', 'New comment')}</span>
        <button onClick={() => setNewOpen(false)} className="icon-btn" style={{ ...railBtn, marginLeft: 'auto' }}
                aria-label={t(lang, 'Свернуть полку', 'Collapse the shelf')}
                title={t(lang, 'Свернуть полку', 'Collapse the shelf')}><Icon name="panel-right-close" size={15} /></button>
      </div>
      {mayDrawHere ? (
        <>
          {/* Инструменты стоят у САМИХ ЛИСТОВ, а не здесь: рисуют по чертежу, и
              полка замечаний им не место — рука уходила через весь экран, а
              полоса в восемь кнопок сминала форму выпуска.

              Распорка прижимает форму КНИЗУ — так же, как в обычном виде: поле
              ввода стоит у нижнего края, под рукой, а не посреди пустой полки. */}
          <div style={{ flex: 1, minHeight: 0 }} />
          {RaiseFormBody}
        </>
      ) : (
        /* Сюда попадают, только выбрав в «Стало» НЕ ту версию, что открыта:
           отсутствие шага рассмотрения работе не мешает — обводку кладут и вне
           круга, а про сам выпуск скажет форма ниже.
           Номер версии не печатается намеренно: под рукой версия ФАЙЛА, а
           список «Стало» подписан ПОДАЧЕЙ — назвали бы число, которого в
           списке нет. */
        <div style={{ padding: 14, display: 'flex', alignItems: 'flex-start', gap: 8,
                      font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
          <span style={{ display: 'inline-flex', marginTop: 1 }}><Icon name="lock" size={14} /></span>
          <span>
            {t(lang, 'Метки ставятся на версию, которую рассматривают, — ту, что открыта в просмотрщике. Выберите её в «Стало».',
                     'Marks are placed on the version under review — the one open in the viewer. Pick it in «After».')}
          </span>
        </div>
      )}
    </div>
    </ShelfSlide>
  )

  /* Сравнивать можно только загруженные версии. Раньше сюда шли блобы текущей
     вкладки, и после перезагрузки сравнивать было нечего. */
  const CompareArea = (
    <CompareLoader no={no} rev={rev} revList={revList} versions={shown} lang={lang} page={page}
      onPages={setPageCount}
      onPage={(step) => jump(page + step)}
      onPair={onPair}
      overlay={compareOverlay}
      shelf={() => CompareRaiseRail}
      leftShelf={(before, after) => (
        <ShelfSlide open={oldOpen} width={oldBox.width}
                    strip={compareStrip('left', 'message-square-warning',
                      t(lang, 'Развернуть замечания прошлой подачи', 'Expand the previous submission comments'),
                      () => setOldOpen(true))}>
        <CompareComments
          box={oldBox}
          crs={crsList} before={before} after={after} lang={lang} page={page}
          me={[me?.user?.name, me?.subject].filter((x): x is string => !!x)}
          canApprove={iApproveHere}
          onlyBefore
          onCollapse={() => setOldOpen(false)}
          focusCrs={oldFocusCode}
          /* Замечание выбрано в полке — подсвечивается его место на СТАРОМ
             листе. Метка ищется по коду в слое той стороны: в полке лежит код
             замечания, а на листе — ключ разметки. */
          onFocusCrs={(code) => {
            const found = (beforeLayer.data ?? []).find((m) => m.crs === code)
            setOldFocus(found?.id ?? null)
            if (found && found.page !== page) { setPage(found.page); setPageDraft(String(found.page)) }
          }}
          canReview={canRaise}
          trm={reviewTrm}
          busy={closeCrs.isPending || editAll.isPending}
          onClose={(code) => closeCrs.mutate({ crs: code, decision: null }, {
            onSuccess: () => toast({ tone: 'success', title: t(lang, 'Замечание закрыто', 'Comment closed'), text: code }),
          })}
          onEditAll={(items, done) => editAll.mutate({ items }, {
            onSuccess: () => {
              done()
              toast({ tone: 'success',
                title: t(lang, `Исправлено замечаний: ${items.length}`, `Comments fixed: ${items.length}`) })
            },
          })}
          /* Новое замечание «на основе» привязывается к НОВОЙ версии, а место
             на листе у него своё: выпуск без булавки невозможен. Поэтому здесь
             не выпуск, а переход в обычный вид на нужной версии с готовым
             текстом — булавку ставит человек. */
          onReissue={(c) => {
            /* Место сбрасывается вместе с версией — ровно как это делают
               переключатели ревизии и версии в панели. Оставленная булавка
               принадлежит РАЗМЕТКЕ прежней пары: её координаты уехали бы на
               чужой лист, а обводка ушла бы запросом по чужому ключу и
               получила 404. */
            setPickedRev(after.rev)
            setPickedVer(after.ver)
            setDraftPin(null)
            setDraftMark(null)
            setPage(1)
            setPageCount(1)
            setDraftText(c.text)
            setDraftCrit(c.crit)
            setVariant('classic')
            setCrsOpen(true)
            toast({ tone: 'info', title: t(lang, 'Текст перенесён', 'Text carried over'),
              text: t(lang, `Поставьте булавку на ${after.rev} ${versionLabel(after.sub, lang)} и выпустите замечание`,
                            `Place a pin on ${after.rev} ${versionLabel(after.sub, lang)} and raise the comment`) })
          }}
        />
        </ShelfSlide>
      )} />
  )

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', background: 'var(--surface-page)' }}>
      {Toolbar}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {variant === 'focus' ? (
          <div style={{ flex: 1, position: 'relative', minWidth: 0, display: 'flex' }}>
            {SheetArea}
            <div style={{ position: 'absolute', left: 16, top: 16 }}>{ToolRail}</div>
            <div style={{ position: 'absolute', right: 16, top: 16, padding: '8px 12px', background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: 'var(--code-3)', display: 'inline-flex' }}><Icon name="map-pin" size={14} /></span>
              {/* Плашка считает текущий лист — она и висит над листом. Панель
                  справа считает документ целиком, поэтому число здесь названо. */}
              <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{t(lang, 'на листе', 'on this sheet')}: {marks.length}</span>
            </div>
          </div>
        ) : variant === 'compare' ? (
          /* Полоса инструментов висит НАД листами, вплотную к полке «Новое
             замечание»: рисуют по правой стороне, и инструмент должен быть у
             руки, а не через экран. Поверх, а не колонкой — колонка отняла бы
             у двух чертежей ещё сорок точек ширины.

             Отступ считается от ПРАВОГО края и от ширины полки, поэтому полоса
             едет за ней: свернули полку до полосы в 38 px — инструменты сами
             сместились вправо. У левого края её быть не может: там полка
             замечаний прошлой подачи, и полоса накрывала бы её. */
          <div style={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex' }}>
            {CompareArea}
            {mayDrawHere ? (
              /* Отступ едет тем же переходом, что ширина полки (ShelfSlide),
                 иначе полоса прыгала бы вперёд полки; на время тяги ручки
                 переход глушится — полоса идёт за курсором. */
              <div style={{ position: 'absolute', right: (newOpen ? raiseBox.width : 38) + 12, top: 56, zIndex: 5,
                            transition: raiseDrag ? 'none' : `right ${newOpen ? 'var(--duration-normal)' : 'var(--duration-fast)'} var(--ease-out)` }}>
                {ToolRail}
              </div>
            ) : null}
          </div>
        ) : (
          <>
            <div style={{ padding: 12, flex: '0 0 auto' }}>{ToolRail}</div>
            {SheetArea}
            <ShelfSlide open={crsOpen} width={crsBox.width} strip={CrsStrip}>{CrsRail}</ShelfSlide>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Подписанные адреса всех версий ревизии. Каждая подпись выдаётся отдельным
 * запросом — хранилище подписывает объект, а не список.
 */
function CompareLoader({ no, rev, revList, versions, lang, page, onPages, onPage, onPair, overlay, leftShelf, shelf }: {
  no: string; rev: string | null
  /** Все ревизии документа: сравнивают чаще всего ревизию с ревизией. */
  revList: string[]
  versions: DocVersion[]; lang: Lang; page: number
  onPages: (n: number) => void
  onPage?: (step: number) => void
  onPair?: (before: CompareItem, after: CompareItem) => void
  overlay?: (side: 'before' | 'after', item: CompareItem) => ReactNode
  leftShelf?: (before: CompareItem, after: CompareItem) => ReactNode
  shelf?: (before: CompareItem, after: CompareItem) => ReactNode
}) {
  const [items, setItems] = useState<CompareItem[] | null>(null)
  useEffect(() => {
    if (!rev) { setItems(null); return }
    let cancelled = false
    const filesOf = (r: string) => `/docs/${encodeURIComponent(no)}/revisions/${encodeURIComponent(r)}/files`
    /* Сравнивают ревизию с ревизией: «что изменилось между A и B» — обычный
       вопрос приёмки, а версии внутри одной ревизии это переиздание одного и
       того же выпуска. Прежде выбор состоял только из версий текущей ревизии,
       и на документе с одной версией сравнивать было нечего.

       Из чужой ревизии берётся её основной файл последней версии: остальные
       версии той ревизии — её внутренняя история. */
    const others = revList.filter((r) => r !== rev).slice(0, 6)
    /* Сравнивают как раз прошлые ревизии, и они помечены: собранная копия
       живёт в памяти вкладки, пока показана, поэтому её адрес отзывается. */
    const created: string[] = []
    const label = (base: string, notice: string | null) => notice ? `${base} · ${notice}` : base
    /* Подача набора, а не номер файла. Замечание помнит ПОДАЧУ, и подписывать
       сторону сравнения счётчиком файлов значит печатать число, которого
       вторая сторона никогда не видела, — и не найти по нему ни одного
       замечания. Перевод — тот же, что в карточке документа. */
    const subsOf = (r: string) =>
      request<DocSubmission[]>('GET', `/docs/${encodeURIComponent(no)}/revisions/${encodeURIComponent(r)}/submissions`)
        .catch(() => [] as DocSubmission[])
    const side = (r: string, ver: number, sub: number | null, file: string, notice: string | null, url: string) =>
      ({ url, rev: r, ver, sub,
         name: label(`${r} ${versionLabel(sub, lang)} · ${file}`, notice) })
    const ownSubs = subsOf(rev)
    const own = versions.map((v, i) =>
      Promise.all([sheetUrl(`${filesOf(rev)}/${v.v}/content`), ownSubs])
        .then(([sheet, subs]) => {
          if (sheet.url.startsWith('blob:')) created.push(sheet.url)
          const sub = submissionOf(versions.map((x) => ({ ver: x.v })), subs)[i] ?? null
          return side(rev, v.v, sub, v.file, sheet.notice, sheet.url)
        })
        .catch(() => null))
    const foreign = others.map((r) =>
      Promise.all([request<DocVersion[]>('GET', `/docs/${encodeURIComponent(no)}/versions/${encodeURIComponent(r)}`), subsOf(r)])
        .then(([rows, subs]) => {
          const main = rows.filter((v) => (v.kind ?? 'main') === 'main')
          const mains = main.length ? main : rows
          const pick = mains.reduce<DocVersion | null>((best, v) => !best || v.v > best.v ? v : best, null)
          if (!pick) return null
          const sub = submissionOf(mains.map((x) => ({ ver: x.v })), subs)[mains.indexOf(pick)] ?? null
          return sheetUrl(`${filesOf(r)}/${pick.v}/content`)
            .then((sheet) => {
              if (sheet.url.startsWith('blob:')) created.push(sheet.url)
              return side(r, pick.v, sub, pick.file, sheet.notice, sheet.url)
            })
        })
        .catch(() => null))
    Promise.all([...own, ...foreign]).then((loaded) => {
      if (!cancelled) setItems(loaded.filter((x): x is CompareItem => x != null))
    })
    return () => {
      cancelled = true
      for (const url of created) URL.revokeObjectURL(url)
    }
  }, [no, rev, revList, versions, lang])

  if (items && items.length >= 2) {
    return <PdfCompare items={items} lang={lang} page={page} onPages={onPages} onPage={onPage}
                        onPair={onPair} overlay={overlay} leftShelf={leftShelf} shelf={shelf} />
  }
  return (
    <div style={{ flex: 1, display: 'grid', placeItems: 'center', background: 'var(--slate-800)', padding: 28 }}>
      <div style={{ maxWidth: 440, textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', marginBottom: 12, color: 'var(--slate-400)' }}><Icon name="arrows-right-left" size={32} /></div>
        <div style={{ font: 'var(--font-card-title)', color: 'var(--slate-100)', marginBottom: 6 }}>{t(lang, 'Нужны две загруженные версии', 'Two uploaded versions needed')}</div>
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--slate-300)' }}>
          {t(lang, `Сравнивать нечего: у документа одна загруженная выкладка. Сравнение показывает разницу между двумя выпусками — ревизией и ревизией или версией и версией; приложите файл второй ревизии в карточке документа.`,
            `Nothing to compare: the document has a single uploaded file. Compare shows the difference between two issues — revision to revision or version to version.`)}
        </div>
      </div>
    </div>
  )
}

/**
 * Обёртка полки: ширина меняется переходом, а не ремонтом.
 *
 * <p>Раньше развёрнутая полка и полоса в 38 px подменяли друг друга мгновенным
 * ремонтом — скачок на треть экрана, и чертёж прыгал вместе с ним. Контейнер
 * живёт постоянно: ширина едет переходом (сворачивание быстрее — уход быстрее
 * входа), содержимое проявляется opacity по смене состояния.
 *
 * <p>На время тяги ручки транзишн глушится обязательно: полка обязана идти за
 * курсором, а не догонять его через 180 мс. Тяга распознаётся по классу ручки
 * (ResizeHandles живут внутри содержимого), а наружу отдаётся onDragging — у
 * полки «Новое замечание» за шириной едет ещё и полоса инструментов.
 */
function ShelfSlide({ open, width, strip, children, onDragging }: {
  open: boolean
  /** Ширина развёрнутой полки — из useResizable её содержимого. */
  width: number
  strip: ReactNode
  children: ReactNode
  onDragging?: (v: boolean) => void
}) {
  const [dragging, setDragging] = useState(false)
  const drag = (v: boolean) => { setDragging(v); onDragging?.(v) }
  return (
    <div
      onPointerDownCapture={(e) => { if (e.target instanceof HTMLElement && e.target.classList.contains('rsz')) drag(true) }}
      onPointerUpCapture={() => drag(false)}
      onPointerCancelCapture={() => drag(false)}
      style={{
        width: open ? width : 38,
        flex: '0 0 auto',
        overflow: 'hidden',
        background: 'var(--surface-card)',
        transition: dragging ? 'none' : `width ${open ? 'var(--duration-normal)' : 'var(--duration-fast)'} var(--ease-out)`,
      }}>
      {/* key перезапускает проявление на каждой смене состояния; flex тянет
          содержимое на высоту полки, ширина у содержимого своя — текст не
          переливается, пока едет контейнер. */}
      <div key={open ? 'shelf' : 'strip'} style={{ height: '100%', display: 'flex', animation: 'fade var(--duration-fast) var(--ease-out)' }}>
        {open ? children : strip}
      </div>
    </div>
  )
}

/**
 * Замечания в режиме сравнения.
 *
 * <p>Полка обычного вида собрана из БУЛАВОК действующей версии: там «замечание»
 * это точка на листе, который сейчас на экране. В сравнении на экране две
 * версии сразу, и вопрос другой — «что было сказано на той, и что на этой».
 * Поэтому здесь список идёт от самих замечаний и группируется подачей, на
 * которой замечание выпущено.
 *
 * <p>Стороны подписаны: «Было» и «Стало». Замечание, выпущенное не на этих
 * двух подачах, показано тускло — оно относится к другому кругу, и путать его
 * с текущим разговором нельзя.
 */
function CompareComments({ crs, before, after, lang, page, me, canApprove, onlyBefore, onCollapse, focusCrs, onFocusCrs, canReview, trm, busy, onClose, onEditAll, onReissue, box }: {
  crs: Crs[]
  before: CompareItem
  after: CompareItem
  lang: Lang
  /** Размер полки. Хук живёт у вызывающего: ширину знает и контейнер перехода. */
  box: Resizable
  /** Лист на экране: замечание своего листа выделено — оно про то, что видно. */
  page: number
  /** Как вошедший назван в реестре замечаний: реестр отдаёт имя, а без карточки
      пользователя — логин. Нужен, чтобы отличить своё замечание от чужого в
      подписи, а не для прав: права проверяет сервер. */
  me: string[]
  canApprove: boolean
  /** Показывать только замечания стороны «Было» — режим левой полки сравнения. */
  onlyBefore?: boolean
  /** Свернуть полку. Есть только у полок сравнения: экран там трёхколоночный. */
  onCollapse?: () => void
  /** Код выделенного замечания — чтобы строка в полке и метка на листе совпадали. */
  focusCrs?: string | null
  /** Выбрать замечание: подсветить его метку на листе своей стороны. */
  onFocusCrs?: (code: string) => void
  /** Открыт ли у вошедшего шаг РАССМОТРЕНИЯ: без круга автор не закрывает. */
  canReview: boolean
  /** Пакет, в котором вошедший держит шаг: правка и решение — только по нему. */
  trm: string | null
  busy: boolean
  onClose: (code: string) => void
  onEditAll: (items: { crs: string; body: string; crit: string; clause: string | null }[],
              done: () => void) => void
  onReissue: (c: Crs) => void
}) {
  /* Черновики правки живут ЗДЕСЬ, а не в запросе: утверждающий переписывает
     десяток формулировок и отправляет их одним нажатием. */
  const [edits, setEdits] = useState<Record<string, string> | null>(null)

  const sideOf = (c: Crs): 'before' | 'after' | null => {
    /* Подача замечания сверяется с ПОДАЧЕЙ набора (`sub`), а не с версией
       ФАЙЛА (`ver`). Это разные счётчики: подача считается с нуля и щёлкает на
       присылке, версия файла — с единицы и на каждой загрузке. Сверка одного с
       другим не совпадает даже в простейшем случае — «0» против «1», — и
       раньше давала лишь неверный значок «было/стало», а как только отбор стал
       жёстким, вырезала бы из полки вообще всё. */
    const same = (it: CompareItem) => c.rev === it.rev && c.ver === it.sub
    return same(before) ? 'before' : same(after) ? 'after' : null
  }
  /* Порядок — по подаче, свежие сверху: разговор идёт о последнем круге.
     Полка слева показывает то, что БЫЛО СКАЗАНО на сравниваемой подаче, а не
     весь документ: вопрос круга — «учтено ли вот это», и лишние строки на него
     отвечать мешают. Замечания без подачи (выпущенные до V16) видны всегда:
     спрятать их значило бы потерять. */
  const rows = [...crs]
    .filter((c) => onlyBefore ? (sideOf(c) === 'before' || c.ver == null) : true)
    .sort((a, b) =>
      (b.rev ?? '').localeCompare(a.rev ?? '') || (b.ver ?? -1) - (a.ver ?? -1) || a.id.localeCompare(b.id))
  /* Править можно ровно то, что сервер примет: замечание ЭТОГО пакета, ещё не
     ушедшее листом. Полка по построению показывает и прошлые круги — то есть
     как раз выпущенные и чужие, — и без отбора одна такая строка отменяла бы
     всю пачку (сервер сохраняет целиком). */
  /* Считается из ПОЛНОГО списка, а не из отобранных строк: правят замечания
     ТЕКУЩЕГО круга, а полка слева показывает прошлую подачу — по построению
     это разные множества, и от фильтра пачечная правка исчезала совсем. */
  const editable = crs.filter((c) => c.trm === trm && !c.sheet
    && c.status !== 'closed' && c.status !== 'withdrawn' && c.verdict !== 'rejected')

  const chip = (c: Crs) => {
    const side = sideOf(c)
    const tone = side === 'before' ? 'neutral' : side === 'after' ? 'accent' : undefined
    const mark = side === 'before' ? t(lang, 'было', 'before') : side === 'after' ? t(lang, 'стало', 'after') : null
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: side ? 'var(--text-strong)' : 'var(--text-subtle)' }}>
          {revVer(c.rev, c.ver)}
        </span>
        {mark ? <Badge tone={tone} size="sm">{mark}</Badge> : null}
      </span>
    )
  }

  return (
    <div ref={box.ref}
         style={{ width: box.width, flex: '0 0 auto', position: 'relative', display: 'flex', flexDirection: 'column', background: 'var(--surface-card)',
                  borderLeft: onlyBefore ? undefined : '1px solid var(--border-default)',
                  borderRight: onlyBefore ? '1px solid var(--border-default)' : undefined }}>
      <ResizeHandles box={box} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
        <span style={{ color: 'var(--accent-ink)', display: 'inline-flex' }}><Icon name="message-square-warning" size={16} /></span>
        <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>
          {onlyBefore ? t(lang, 'Сказано на этой подаче', 'Said on this submission') : t(lang, 'Замечания по версиям', 'Comments by version')}
        </span>
        <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>{rows.length}</span>
        {onCollapse ? (
          <button onClick={onCollapse} className="icon-btn" style={railBtn}
                  aria-label={t(lang, 'Свернуть полку', 'Collapse the shelf')}
                  title={t(lang, 'Свернуть полку', 'Collapse the shelf')}><Icon name="panel-left-close" size={15} /></button>
        ) : null}
      </div>

      {/* Пачечная правка — работа утверждающего: он сводит замечания
          рецензентов в один лист, и лист уходит за его подписью. */}
      {canApprove && editable.length ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface-sunken)' }}>
          {edits ? (
            <>
              <Button size="sm" variant="primary" disabled={busy}
                      onClick={() => {
                        const items = editable
                          .filter((c) => (edits[c.id] ?? c.text).trim() && (edits[c.id] ?? c.text) !== c.text)
                          .map((c) => ({ crs: c.id, body: edits[c.id]!.trim(), crit: c.crit, clause: c.clause || null }))
                        if (!items.length) { setEdits(null); return }
                        /* Панель гаснет ТОЛЬКО при успехе: сервер сохраняет
                           пачку целиком, и при отказе переписанные
                           формулировки обязаны остаться на экране — иначе
                           «поправит и пошлёт снова» невыполнимо. */
                        onEditAll(items, () => setEdits(null))
                      }}>{t(lang, 'Сохранить все', 'Save all')}</Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEdits(null)}>{t(lang, 'Отмена', 'Cancel')}</Button>
              <span style={{ marginLeft: 'auto', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
                {t(lang, 'сохраняется целиком', 'saved as one')}
              </span>
            </>
          ) : (
            <Button size="sm" variant="ghost" iconLeft={<Icon name="pencil" size={13} />}
                    onClick={() => setEdits(Object.fromEntries(editable.map((c) => [c.id, c.text])))}>
              {t(lang, 'Править формулировки', 'Fix the wording')}
            </Button>
          )}
        </div>
      ) : null}

      <div style={{ flex: 1, overflow: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Выбранная строка красится инлайновым accent-soft, а hover/active
            .tdms-rail из ui.css идут с !important и перебивали его — строка
            выглядела невыбранной сразу после клика. Override локальный
            (ui.css правит другой слой): [data-chosen] поднимает специфичность,
            фон выбранной не меняется ни наведением, ни нажатием. */}
        <style>{`.tdms-rail[data-chosen]:not(:disabled):active { background: var(--accent-soft) !important; }
@media (hover: hover) and (pointer: fine) { .tdms-rail[data-chosen]:hover { background: var(--accent-soft) !important; } }`}</style>
        {rows.map((c) => {
          const side = sideOf(c)
          const mine = me.includes(c.author)
          /* Снятое замечание не закрывают и не правят: исправлять по нему
             нечего, оно выпущено по ошибке. Без своей ветки оно рисовалось бы
             жёлтым «открыто» и получало кнопки, на которые сервер отвечает
             409 (та же ветка есть в списке замечаний). */
          const open = c.status !== 'closed' && c.status !== 'withdrawn' && c.verdict !== 'rejected'
          const chosen = focusCrs != null && focusCrs === c.id
          return (
            <div key={c.id}
                 {...(onFocusCrs ? clickable(() => onFocusCrs(c.id)) : {})}
                 /* Кликабельная строка отзывается как строка (hover/active из
                    ui.css, паттерн Admin), некликабельная — остаётся текстом. */
                 className={onFocusCrs ? 'tdms-rail' : undefined}
                 data-chosen={chosen || undefined}
                 title={onFocusCrs ? t(lang, 'Показать место на листе', 'Show the spot on the sheet') : undefined}
                 style={{
                   border: '1px solid ' + (chosen ? 'var(--accent)' : side === 'after' ? 'var(--accent)' : 'var(--border-default)'),
                   borderLeft: '3px solid ' + (side ? 'var(--code-3)' : 'var(--border-default)'),
                   borderRadius: 'var(--radius-sm)', padding: '10px 12px',
                   background: chosen ? 'var(--accent-soft)' : undefined,
                   cursor: onFocusCrs ? 'pointer' : undefined,
                   opacity: side ? 1 : 0.62,
                 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--text-strong)' }}>{c.id}</span>
                <CritBadge level={c.crit} lang={lang} />
                {chip(c)}
                {c.page === page ? <Badge tone="info" size="sm">{t(lang, 'этот лист', 'this sheet')}</Badge> : null}
              </div>
              {edits && edits[c.id] != null ? (
                <Input value={edits[c.id]} onChange={(e) => setEdits({ ...edits, [c.id]: e.target.value })}
                       style={{ marginTop: 7 }} />
              ) : (
                <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', marginTop: 6 }}>{c.text}</div>
              )}
              {c.acsr ? (
                <div style={{ marginTop: 6, padding: '6px 8px', borderRadius: 'var(--radius-sm)', background: 'var(--surface-sunken)' }}>
                  <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginBottom: 2 }}>
                    {t(lang, 'Ответ подрядчика', 'Contractor answer')} · {c.acsr.date ?? ''}
                  </div>
                  <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{c.acsr.text}</div>
                </div>
              ) : null}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 7 }}>
                <Badge tone={c.status === 'closed' ? 'success' : c.status === 'withdrawn' ? 'neutral'
                  : c.status === 'answered' ? 'info' : 'warning'} size="sm">
                  {c.status === 'closed' ? t(lang, 'закрыто', 'closed')
                    : c.status === 'withdrawn' ? t(lang, 'снято', 'withdrawn')
                    : c.status === 'answered' ? t(lang, 'есть ответ', 'answered')
                    : t(lang, 'открыто', 'open')}
                </Badge>
                {c.verdict === 'rejected' ? <Badge tone="neutral" size="sm">{t(lang, 'снято', 'rejected')}</Badge> : null}
                {/* «Ваше» — подпись, а не право: закрывает всё равно
                    утверждающий. На документ смотрят несколько рецензентов
                    сразу, и своё замечание в общем списке искать глазами. */}
                {mine ? <Badge tone="info" size="sm">{t(lang, 'ваше', 'yours')}</Badge> : null}
                <span style={{ marginLeft: 'auto', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{c.author}</span>
              </div>
              {/* Закрывает утверждающий — и автор, но только на СВОЁМ
                  замечании ПРОШЛОГО круга, пока идёт новый: подрядчик подал
                  следующую версию и ответил, и проверить исправление способен
                  ровно тот, кто замечание написал. Своё замечание текущего
                  круга автор не закрывает — на него ещё не отвечали. То же
                  правило держит сервер (CrsCommands.requireDecider).

                  Выпуск «на основе» открыт всем, кто вправе выпускать: сам
                  выпуск случится в обычном виде, с булавкой. */}
              {open && !edits ? (
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  {canApprove || (mine && c.trm !== trm && canReview) ? (
                    <Button size="sm" variant="ghost" disabled={busy} iconLeft={<Icon name="check" size={13} />}
                            title={t(lang, 'Исправление проверено — замечание закрывается', 'The fix is verified — the comment closes')}
                            onClick={() => onClose(c.id)}>{t(lang, 'Закрыть', 'Close')}</Button>
                  ) : null}
                  <Button size="sm" variant="ghost" disabled={busy} iconLeft={<Icon name="message-square-plus" size={13} />}
                          title={t(lang, `Новое замечание на основе этого — на ${revVer(after.rev, after.ver)}`,
                                        `A new comment based on this one — on ${revVer(after.rev, after.ver)}`)}
                          onClick={() => onReissue(c)}>{t(lang, 'На основе', 'Reissue')}</Button>
                </div>
              ) : null}
            </div>
          )
        })}
        {rows.length === 0 ? (
          <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)', padding: '8px 2px' }}>
            {onlyBefore
              ? t(lang, 'На этой подаче замечаний не было', 'No comments were raised on this submission')
              : t(lang, 'По документу замечаний нет', 'No comments on this document')}
          </div>
        ) : null}
      </div>
    </div>
  )
}

/** Кнопка сворачивания полки: та же клетка, что у кнопок полосы инструментов. */
const railBtn: CSSProperties = { display: 'grid', placeItems: 'center', width: 26, height: 26, border: 0, borderRadius: 'var(--radius-sm)', background: 'transparent', color: 'var(--text-subtle)', cursor: 'pointer' }

const hdrBtn: CSSProperties = { display: 'grid', placeItems: 'center', width: 30, height: 30, border: '1px solid var(--border-header)', borderRadius: 'var(--radius-sm)', background: 'transparent', color: 'var(--slate-300)', cursor: 'pointer' }
